import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PropositionDate from '../components/PropositionDate';
import { proprietesLien } from '../services/fichiers';
import { aujourdhui } from '../services/dates';
import { rechercheFiche, getFiche, cocherFiche, getQualification, saveQualification,
         apercuActionFiche, envoyerReportRdv, abandonnerWo,
         saveArcArs, getPiecesDossier, deposerPiece, supprimerPiece, definirDureeFiche,
         ecrireChampsFiche, getMetaChampsFiche, getTechniciens,
         getErdvFiche, validerErdvFiche,
         creerDevisDossier, setLignesDevisDossier } from '../services/api';

/**
 * Fiche dossier — le remplacement de la page Zoho.
 *
 * Miroir en lecture pour l'essentiel : tout ce qui vient de Zoho s'affiche
 * sans pouvoir être modifié ici, tant que la bascule n'a pas eu lieu. Trois
 * exceptions : la qualification SLA, qui est à nous, la case qui déclenche le
 * mail de proposition de RDV, et les deux actions de l'onglet Planification —
 * reporter un rendez-vous, abandonner un work order — dont nous envoyons
 * nous-mêmes le mail et posons l'étape.
 *
 * La bande d'identité change de couleur selon le type — ambre pour la
 * production et la visite technique, bleu-vert pour le SAV — pour qu'on sache
 * où l'on est avant d'avoir lu.
 */

const AMBRE = { fond: 'linear-gradient(90deg,#E3B04B 0%,#EBC878 62%,#F0D9A4 100%)', label: '#6B5415' };
const TEAL  = { fond: 'linear-gradient(90deg,#4F9FA3 0%,#86C2C4 60%,#B4DADB 100%)', label: '#0B4447' };

// Familles d'étapes : on lit l'état sans distinguer les quinze nuances.
const COULEUR_ETAPE = {
  'A planifier': ['#E8E6E0', '#44413B'],
  'Planifiée': ['#DCE7FB', '#1E3A8A'],
  'En attente validation Admin': ['#FBEFD6', '#7C5A10'],
  'En attente validation DEVIS': ['#FBEFD6', '#7C5A10'],
  'En attente validation DOSTEC/ARC': ['#FBEFD6', '#7C5A10'],
  'CLEARED': ['#D6F2E0', '#14532D'],
  'CLEARED suite reprise': ['#D6F2E0', '#14532D'],
  'VT OK': ['#D3EFEF', '#0B4447'],
  'FAILED': ['#FAD9D9', '#7F1D1D'],
  'RDVNH': ['#FAD9D9', '#7F1D1D'],
  'CR non conforme': ['#FBDFD0', '#7C2D12'],
  'CRI non cloturé': ['#FBDFD0', '#7C2D12'],
  'IAT': ['#E6DCF5', '#4C1D95'],
  'Commande gelée': ['#DDE3EA', '#334155'],
  'KO - Commande annulée CO': ['#D6D4CF', '#2B2A26'],
};

const SECTIONS = [
  ['synthese', 'Synthèse'], ['pbo', 'PBO'], ['pm', 'PM'], ['pto', 'PTO'], ['ont', 'ONT'],
  ['longueurs', 'Longueurs'], ['contraintes', 'Contraintes'], ['batiment', 'Infos bâtiment'],
];

const jour = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('fr-FR',
  { weekday: 'long', day: '2-digit', month: 'long' }) : '—';

/**
 * Une date Zoho rendue lisible.
 *
 * Le module sert « 2026-09-24T10:57:00+02:00 », qui se lit mal et occupe
 * toute la colonne. On n'en garde que le jour et l'heure.
 */
function fmtDT(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  const j = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const h = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${j} ${h}`;
}

/**
 * Un booléen montré comme la case qu'il deviendra.
 *
 * La page est en lecture seule : la case est désactivée, mais la montrer dès
 * maintenant dit où l'on pourra cocher une fois l'écriture ouverte, là où un
 * « oui / non » laisserait croire à un champ figé.
 */
/**
 * Une case a cocher qui ecrit dans Zoho.
 *
 * Sans confirmation, contrairement aux deux cases qui declenchent un mail :
 * celles-ci sont des conditions ou des constats, les cocher n'envoie rien par
 * soi-meme. L'etat revient a sa valeur d'origine si l'ecriture echoue, plutot
 * que de laisser croire a un enregistrement.
 */
function CocheEditable({ dossierId, champ, nom, valeur, aide, onEcrit }) {
  const [etat, setEtat] = useState(!!valeur);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [fait, setFait] = useState(false);

  useEffect(() => { setEtat(!!valeur); }, [valeur]);

  const basculer = async () => {
    const vers = !etat;
    setEtat(vers); setEnCours(true); setErreur(null);
    try {
      await ecrireChampsFiche(dossierId, { [champ]: vers });
      setFait(true);
      setTimeout(() => setFait(false), 2200);
      if (onEcrit) onEcrit();
    } catch (e) {
      setEtat(!vers);
      setErreur(e.response?.data?.error || 'Écriture impossible.');
    } finally { setEnCours(false); }
  };

  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                      cursor: enCours ? 'progress' : 'pointer' }}>
        <input type="checkbox" checked={etat} disabled={enCours} onChange={basculer}
          style={{ width: 16, height: 16, marginTop: 1, accentColor: 'var(--accent)',
                   flexShrink: 0, cursor: enCours ? 'progress' : 'pointer' }} />
        <span>
          <span style={{ fontSize: 13, display: 'block' }}>{nom}</span>
          {aide && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{aide}</span>}
        </span>
      </label>
      {erreur && <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: '#b91c1c' }}>{erreur}</p>}
      {fait && <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: 'var(--green2)', fontWeight: 700 }}>✓ enregistré</p>}
    </div>
  );
}

/**
 * Un champ modifiable, qui ecrit dans Zoho.
 *
 * La valeur ne part qu'a la validation, jamais a la frappe : une ecriture par
 * caractere multiplierait les appels et pourrait declencher un workflow a
 * chaque lettre. L'echappement annule, Entree valide sur une ligne simple.
 *
 * Un refus de conflit remonte tel quel : l'empreinte a vu qu'un tiers avait
 * touche le champ depuis l'ouverture de la fiche, et il vaut mieux recharger
 * que d'ecraser le travail de quelqu'un d'autre.
 */
function Modifiable({ dossierId, champ, valeur, options, type = 'texte', rendu, style, onEcrit }) {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState(valeur ?? '');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [fait, setFait] = useState(false);

  // Zoho rend « 2026-10-09T15:40:00+02:00 », l'input datetime-local veut
  // « 2026-10-09T15:40 » : on coupe, et le serveur recollera le fuseau.
  useEffect(() => {
    const v = valeur ?? '';
    setSaisie(type === 'horodatage' ? String(v).slice(0, 16) : v);
  }, [valeur, type]);

  const valider = async (confirme) => {
    const vers = String(saisie ?? '');
    if (vers === String(valeur ?? '')) { setEdition(false); setErreur(null); return; }
    setEnCours(true); setErreur(null);
    try {
      await ecrireChampsFiche(dossierId, { [champ]: vers }, confirme);
      setEdition(false);
      // Une coche verte deux secondes : sans elle, un enregistrement reussi
      // ne se distingue pas d'un clic perdu.
      setFait(true);
      setTimeout(() => setFait(false), 2200);
      if (onEcrit) onEcrit();
    } catch (e) {
      const rep = e.response?.data;
      // Changer le technicien ou la date peut retirer le dernier qui pouvait
      // tenir un créneau déjà réservé ailleurs.
      if (rep?.confirmation_requise) {
        setEnCours(false);
        if (confirmerPerte(rep, 'Cette modification')) return valider(true);
        setErreur('Modification annulée.');
        return;
      }
      setErreur(rep?.error || 'Écriture impossible.');
    } finally { setEnCours(false); }
  };

  const annuler = () => { setSaisie(valeur ?? ''); setEdition(false); setErreur(null); };

  if (!edition) {
    return (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', ...style }}>
        {rendu ? rendu(valeur) : (valeur || <span style={{ opacity: .55 }}>—</span>)}
        {/* Le crayon etait trop discret : on ne le voyait pas, donc on croyait
            le champ verrouille. */}
        <button type="button" onClick={() => setEdition(true)} title="Modifier"
          style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 5,
                   cursor: 'pointer', padding: '1px 5px', fontSize: 11, lineHeight: 1.3,
                   color: 'var(--text2)', flexShrink: 0 }}>✎</button>
        {fait && <span style={{ fontSize: 11, color: 'var(--green2)', fontWeight: 700 }}>✓ enregistré</span>}
        {erreur && <span style={{ fontSize: 11, color: '#b91c1c' }}>{erreur}</span>}
      </span>
    );
  }

  const commun = {
    autoFocus: true,
    disabled: enCours,
    value: saisie,
    onChange: e => setSaisie(e.target.value),
    onKeyDown: e => {
      if (e.key === 'Escape') annuler();
      if (e.key === 'Enter' && type !== 'texte_long') valider();
    },
    style: {
      fontSize: 13, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)',
      background: 'var(--bg2)', color: 'var(--text)', minWidth: type === 'heure' ? 90 : 150,
      width: type === 'texte_long' ? '100%' : undefined,
    },
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                   width: type === 'texte_long' ? '100%' : undefined }}>
      {options
        ? <select {...commun}>
            <option value="">—</option>
            {options.map(o => (
              <option key={o.valeur ?? o} value={o.valeur ?? o}>{o.libelle ?? o}</option>
            ))}
          </select>
        : type === 'texte_long'
          ? <textarea {...commun} rows={3} />
          : <input {...commun} type={type === 'date' ? 'date'
              : type === 'heure' ? 'time'
              : type === 'horodatage' ? 'datetime-local' : 'text'} />}
      <button type="button" onClick={valider} disabled={enCours} className="btn btn-primary btn-sm">
        {enCours ? '…' : 'Enregistrer'}
      </button>
      <button type="button" onClick={annuler} disabled={enCours}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text3)' }}>
        annuler
      </button>
      {erreur && (
        <span style={{ fontSize: 11, color: '#b91c1c', flex: '1 1 100%' }}>{erreur}</span>
      )}
    </span>
  );
}

function Coche({ nom, valeur, aide }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, opacity: .95 }}>
      <input type="checkbox" checked={!!valeur} disabled readOnly
        style={{ width: 16, height: 16, marginTop: 1, accentColor: 'var(--accent)', flexShrink: 0 }} />
      <span>
        <span style={{ fontSize: 13, display: 'block' }}>{nom}</span>
        {aide && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{aide}</span>}
      </span>
    </label>
  );
}

/**
 * Ce que les confirmations annoncent avant d'écrire.
 *
 * Les deux textes disent ce que le template Zoho reprend réellement, relevé
 * dans le CRM : le mail de proposition imprime les deux dates d'appel sans
 * succès, celui de suppression imprime le commentaire de planification comme
 * motif. Un champ vide laisse donc un blanc dans un mail déjà parti. On
 * prévient sans bloquer : le process appartient à la conduite d'activité.
 */
function confirmationPropositionRDV(planif) {
  const manque = [
    !planif.premier_appel && 'le 1er appel',
    !planif.second_appel && 'le 2e appel',
  ].filter(Boolean);
  // Rien à signaler : on n'impose pas une boîte de dialogue pour rien.
  if (!manque.length) return null;
  return 'Le mail de proposition annonce au client les deux appels restés sans réponse : '
    + '« nous avons tenté de vous joindre sans succès le …, puis le … ».'
    + '\n\n' + manque.join(' et ')
    + (manque.length > 1 ? ' ne sont pas renseignés' : ' n’est pas renseigné')
    + ' — le mail partira avec un blanc à cet endroit.'
    + '\n\nEnvoyer quand même ?';
}

/** Le cadre commun des deux actions : un bouton qui ouvre, et rien au clic. */
function Action({ libelle, couleur, ouvert, onBasculer, children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
      <button type="button" onClick={onBasculer}
        style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', cursor: 'pointer',
                 padding: '10px 13px', border: 'none', background: ouvert ? 'var(--bg3)' : 'transparent',
                 textAlign: 'left', fontSize: 13, fontWeight: 600, color: couleur }}>
        <span style={{ fontSize: 11 }}>{ouvert ? '▾' : '▸'}</span>
        {libelle}
      </button>
      {ouvert && <div style={{ padding: '13px', borderTop: '1px solid var(--border)' }}>{children}</div>}
    </div>
  );
}

/** L'aperçu du mail, refait quand la saisie se stabilise. */
function ApercuAction({ dossierId, corps: requete }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;
    // Chaque aperçu relit le dossier dans Zoho : on attend que la frappe
    // s'arrête plutôt que d'appeler à chaque caractère.
    const t = setTimeout(() => {
      setErreur(null);
      apercuActionFiche(dossierId, requete)
        .then(d => { if (vivant) setData(d); })
        .catch(e => { if (vivant) setErreur(e.response?.data?.error || 'Aperçu impossible'); });
    }, 700);
    return () => { vivant = false; clearTimeout(t); };
  }, [dossierId, JSON.stringify(requete)]);

  if (erreur) return <p style={{ fontSize: 11, color: '#b91c1c', margin: '10px 0 0' }}>{erreur}</p>;
  if (!data) return <p style={{ fontSize: 11, color: 'var(--text3)', margin: '10px 0 0' }}>Rendu du mail…</p>;

  if (data.sans_mail) {
    return (
      <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: 'var(--text2)',
                  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px' }}>
        Aucun mail ne part pour ce motif : Covage en est l’auteur. Le dossier passera en
        « {data.etape} » et gardera la trace de la demande.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: 'var(--text3)' }}>À : </span>
        {data.destinataires.length
          ? data.destinataires.join(', ')
          : <span style={{ color: '#b91c1c' }}>aucun destinataire</span>}
      </div>
      <div style={{ fontSize: 11, marginBottom: 7 }}>
        <span style={{ color: 'var(--text3)' }}>Objet : </span><strong>{data.sujet}</strong>
      </div>
      <iframe title="aperçu" srcDoc={data.corps}
        style={{ width: '100%', height: 300, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
    </div>
  );
}

/**
 * Le report du rendez-vous.
 *
 * Le mail imprime la date du dossier comme nouvelle date : tant que le dossier
 * porte celle que le client connaît, l'envoi est refusé — annoncer comme
 * nouvelle une date déjà connue ne reporte rien.
 */
function BlocReport({ d, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [source, setSource] = useState('Technicien');
  const [cause, setCause] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  const annoncee = d.derniere_date_annoncee;
  const dateDossier = d.rdv.date_posee;
  const dateInchangee = !!(annoncee && annoncee.date === dateDossier);
  const pret = !!cause.trim() && !dateInchangee && !enCours;

  // La case conditionne la confirmation au client. Décochée, le report part
  // seul : le mail de parsing arrive chez Covage et le client reste sur
  // l'ancienne date. Le dire après coup ne sert à rien — le parsing est déjà
  // parti — donc on le dit dans la demande de confirmation.
  const clientPrevenu = !!d.planification.mail_confirmation_client;

  const envoyer = async () => {
    const entete = `Envoyer le report ?\n\nNouvelle date annoncée : ${jour(dateDossier)}`
      + `${d.rdv.heure_posee ? ' à ' + d.rdv.heure_posee : ''}\nSource : ${source}\n\n`;
    if (!window.confirm(entete + (clientPrevenu
      ? 'Deux mails partent : le report à Covage, et la confirmation de la '
        + 'nouvelle date au client.'
      : '⚠ La case « Mail de confirmation client autorisé » est DÉCOCHÉE.\n\n'
        + 'Seul le report partira, chez Covage. Le client restera sur l’ancienne '
        + 'date.\n\nAnnulez, cochez la case plus bas, puis renvoyez — ou '
        + 'continuez si c’est voulu.'))) return;
    setEnCours(true); setMsg(null);
    try {
      const r = await envoyerReportRdv(d.id, { source, cause: cause.trim() });
      const lignes = [`Report envoyé à ${r.destinataires.join(', ')}.`];
      for (const c of (r.confirmations || [])) {
        if (c.ignore) lignes.push(`Client non prévenu : ${c.ignore}`);
        else if (c.erreur) lignes.push(`Confirmation au client en échec : ${c.erreur}`);
        else if (c.doublon) lignes.push('Confirmation au client déjà partie à l’instant.');
        else lignes.push(`Confirmation de la nouvelle date envoyée à ${(c.destinataires || []).join(', ')}.`);
      }
      // Un report parti sans sa confirmation laisse le client sur l'ancienne
      // date : ça ne doit pas s'afficher en vert.
      const incomplet = (r.confirmations || []).some(c => c.erreur || c.ignore);
      setMsg([incomplet ? 'ko' : 'ok', lignes.join(' ')]);
      setCause('');
      if (onFait) onFait();
    } catch (e) {
      setMsg(['ko', e.response?.data?.error || 'Envoi impossible.']);
    } finally { setEnCours(false); }
  };

  return (
    <Action libelle="Reporter le rendez-vous…" couleur="#7C5A10"
      ouvert={ouvert} onBasculer={() => setOuvert(o => !o)}>

      <div style={{ display: 'grid', gap: 11 }}>
        <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text2)' }}>
          Nouvelle date annoncée : <strong>{dateDossier ? jour(dateDossier) : 'aucune'}</strong>
          {d.rdv.heure_posee ? ` à ${d.rdv.heure_posee}` : ''} — c’est la date du dossier qui part.
          <br />
          Deux mails partent : le <strong>report</strong> au service delivery de Covage, et la{' '}
          <strong>confirmation de la nouvelle date</strong> au client, le même que lors d’une
          planification.
        </div>

        {dateInchangee && (
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: '#b91c1c', background: 'rgba(185,28,28,.07)',
                      border: '1px solid rgba(185,28,28,.3)', borderRadius: 8, padding: '9px 12px' }}>
            Le client a déjà été prévenu pour le {jour(annoncee.date)}. Replanifiez le dossier avant
            d’envoyer le report, sinon le mail annoncerait comme nouvelle la date qu’il connaît déjà.
          </p>
        )}
        {!annoncee && (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text3)' }}>
            Aucun mail de confirmation retrouvé pour ce dossier : vérifiez que la date ci-dessus est bien la nouvelle.
          </p>
        )}

        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Source du report</div>
          <select value={source} onChange={e => setSource(e.target.value)}
            style={{ fontSize: 13, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg)', color: 'var(--text)' }}>
            <option>Technicien</option>
            <option>Client</option>
          </select>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Cause du report</div>
          <textarea value={cause} onChange={e => setCause(e.target.value)} rows={2}
            placeholder="Ce que Covage lira comme cause du report"
            style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, resize: 'vertical',
                     border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
        </div>

        {cause.trim() && !dateInchangee && (
          <ApercuAction dossierId={d.id} corps={{ action: 'report', source, cause: cause.trim() }} />
        )}

        <div>
          <button type="button" disabled={!pret} onClick={envoyer} className="btn btn-primary btn-sm"
            style={{ opacity: pret ? 1 : .45, cursor: pret ? 'pointer' : 'not-allowed' }}>
            {enCours ? 'Envoi…' : 'Envoyer le report à Covage'}
          </button>
          {!cause.trim() && !dateInchangee && (
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 9 }}>la cause est obligatoire</span>
          )}
        </div>

        {msg && <p style={{ margin: 0, fontSize: 12, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</p>}
      </div>
    </Action>
  );
}

/**
 * L'abandon du work order.
 *
 * Le motif commande tout : le mail, l'étape, et la sortie du SLA. Le bloc ne
 * déclenche rien à l'ouverture — c'était le défaut de la case à cocher, qui
 * partait avant qu'on ait choisi le motif.
 */
function BlocAbandon({ d, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const motifs = d.motifs_abandon || [];
  const [motifId, setMotifId] = useState((motifs.find(m => m.defaut) || motifs[0] || {}).id);
  const motif = motifs.find(m => m.id === motifId) || null;
  const [cause, setCause] = useState(motif ? motif.texte : '');
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  // Changer de motif remet son texte : c'est le motif qui porte la phrase.
  const choisir = (id) => {
    setMotifId(id);
    const m = motifs.find(x => x.id === id);
    setCause(m ? m.texte : '');
    setMsg(null);
  };

  if (d.abandon) {
    return (
      <Action libelle="Work order abandonné" couleur="var(--text2)"
        ouvert={ouvert} onBasculer={() => setOuvert(o => !o)}>
        <div style={{ display: 'grid', gap: 7, fontSize: 12, lineHeight: 1.6 }}>
          <div><strong>{d.abandon.libelle}</strong> — le {new Date(d.abandon.at).toLocaleString('fr-FR')} par {d.abandon.par}</div>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px' }}>
            {d.abandon.cause}
          </div>
          <div style={{ color: 'var(--text3)' }}>
            Étape posée : {d.abandon.etape} ·{' '}
            {d.abandon.mail_envoye
              ? `mail envoyé à ${d.abandon.destinataires.join(', ')}`
              : 'aucun mail envoyé, Covage en était l’auteur'}
          </div>
        </div>
      </Action>
    );
  }

  const pret = !!cause.trim() && !enCours && !!motif;

  const envoyer = async () => {
    const question = motif.envoi === 'aucun'
      ? `Enregistrer l’annulation et passer le dossier en « ${motif.etape} » ?\n\nAucun mail ne partira.`
      : `Envoyer le mail à Covage et passer le dossier en « ${motif.etape} » ?\n\nMotif : ${motif.libelle}`;
    if (!window.confirm(question)) return;
    setEnCours(true); setMsg(null);
    try {
      const r = await abandonnerWo(d.id, { motif: motifId, cause: cause.trim() });
      setMsg(['ok', r.mail
        ? `Mail envoyé à ${r.mail.destinataires.join(', ')}, dossier en « ${r.abandon.etape} ».`
        : `Annulation enregistrée, dossier en « ${r.abandon.etape} ».`]);
      if (onFait) onFait();
    } catch (e) {
      setMsg(['ko', e.response?.data?.error || 'Abandon impossible.']);
    } finally { setEnCours(false); }
  };

  return (
    <Action libelle="Abandonner le work order…" couleur="#b91c1c"
      ouvert={ouvert} onBasculer={() => setOuvert(o => !o)}>

      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>Motif</div>
          <div style={{ display: 'grid', gap: 5 }}>
            {motifs.map(m => (
              <label key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name={'motif-' + d.id} checked={m.id === motifId}
                  onChange={() => choisir(m.id)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
                <span>
                  <span style={{ fontSize: 13 }}>{m.libelle}</span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', display: 'block' }}>
                    → {m.etape}
                    {m.hors_sla ? ' · sorti du SLA de planification' : ''}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', display: 'block' }}>
                    {m.envoi === 'aucun'
                      ? 'aucun mail'
                      : <>mail « {m.mail_nom} » → {(m.mail_destinataires || []).join(', ') || 'aucun destinataire'}</>}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
            Cause transmise à Covage — {motif && motif.libre ? 'à écrire' : 'modifiable'}
          </div>
          {/* Le motif « Autre » n'apporte aucune phrase : c'est la conduite
              d'activite qui dit ce qui s'est passe, et le bouton reste
              desactive tant qu'elle ne l'a pas ecrit. */}
          <textarea value={cause} onChange={e => setCause(e.target.value)} rows={motif && motif.libre ? 3 : 2}
            placeholder={motif && motif.libre
              ? 'Ce qui empêche la prise de rendez-vous — par exemple : numéro du contact client erroné, ou client non prévenu du rendez-vous par son opérateur.'
              : undefined}
            style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7, resize: 'vertical',
                     border: `1px solid ${motif && motif.libre && !cause.trim() ? 'var(--accent)' : 'var(--border)'}`,
                     background: 'var(--bg)', color: 'var(--text)' }} />
        </div>

        {cause.trim() && (
          <ApercuAction dossierId={d.id} corps={{ action: 'abandon', motif: motifId, cause: cause.trim() }} />
        )}

        <div>
          <button type="button" disabled={!pret} onClick={envoyer} className="btn btn-primary btn-sm"
            style={{ opacity: pret ? 1 : .45, cursor: pret ? 'pointer' : 'not-allowed' }}>
            {enCours ? 'Envoi…' : (motif && motif.envoi === 'aucun'
              ? 'Enregistrer l’annulation'
              : 'Envoyer et fermer le dossier')}
          </button>
        </div>

        {msg && <p style={{ margin: 0, fontSize: 12, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</p>}
      </div>
    </Action>
  );
}

/**
 * Une case qui écrit vraiment dans Zoho, et dont le workflow envoie un mail.
 *
 * D'où la confirmation : un clic de trop part chez le client ou chez Covage,
 * et rien ne le rappelle. Décocher ensuite ne fait que mentir sur l'état du
 * dossier, c'est dit au moment où on le tente.
 */
function CocheAction({ dossierId, champ, nom, aide, confirmations, valeur }) {
  const [etat, setEtat] = useState(!!valeur);
  const [envoi, setEnvoi] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setEtat(!!valeur); }, [valeur]);

  const cliquer = async (confirme) => {
    const vers = !etat;
    const question = vers ? confirmations.cocher : confirmations.decocher;
    if (!confirme && question && !window.confirm(question)) return;
    setEnvoi(true); setMsg(null);
    try {
      const rep = await cocherFiche(dossierId, champ, vers, confirme);
      setEtat(vers);
      setMsg(['ok', rep.inchange
        ? 'Zoho était déjà dans cet état, rien n’a été renvoyé.'
        : (vers ? 'Enregistré dans Zoho — le workflow est parti.' : 'Case décochée dans Zoho.')]);
    } catch (e) {
      const rep = e.response?.data;
      // Un horaire hors grille Covage — une intervention de nuit — n'empêche
      // rien, mais on le dit avant d'envoyer.
      if (rep?.confirmation_requise) {
        setEnvoi(false);
        if (window.confirm(rep.error + '\n\nEnvoyer la proposition quand même ?')) {
          return cliquer(true);
        }
        setMsg(['ko', 'Proposition non envoyée.']);
        return;
      }
      setMsg(['ko', rep?.error || 'Écriture impossible.']);
    } finally { setEnvoi(false); }
  };

  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                      cursor: envoi ? 'progress' : 'pointer' }}>
        <input type="checkbox" checked={etat} disabled={envoi} onChange={() => cliquer(false)}
          style={{ width: 16, height: 16, marginTop: 1, accentColor: 'var(--accent)',
                   flexShrink: 0, cursor: envoi ? 'progress' : 'pointer' }} />
        <span>
          <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{nom}</span>
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>{aide}</span>
        </span>
      </label>
      {msg && (
        <p style={{ margin: '5px 0 0 24px', fontSize: 11, lineHeight: 1.45,
                    color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</p>
      )}
    </div>
  );
}

/**
 * Dépôt manuel d'une pièce, rangée sur notre VPS.
 *
 * Rien ne part chez Zoho : les pièces vivent dans data/pj/<dossier>/, le même
 * magasin que les comptes rendus des techniciens. C'est le choix de fond —
 * Zoho doit disparaître, les fichiers ne doivent pas partir avec lui.
 */
/**
 * Un devis monté à la main, depuis la fiche.
 *
 * Le devis se remplit tout seul quand un compte rendu le déclenche. Mais on en
 * demande parfois un sans intervention — pour chiffrer une visite technique,
 * ou pour répondre à Covage avant d'envoyer qui que ce soit. Il fallait alors
 * rouvrir le modèle vierge et retaper la commande, le work order, l'opérateur,
 * l'adresse et la PTO : tout ce que cette page affiche déjà.
 *
 * L'en-tête vient donc du dossier. Reste à dire quel raccordement est devisé
 * et quelles longueurs : le barème en déduit l'option 60.3, le câble
 * supplémentaire et, sur le modèle SAV, le tirage au forfait. Les autres
 * prestations se saisissent dans la grille, et le détail dit pourquoi chaque
 * ligne automatique est là — ou pourquoi elle n'y est pas.
 *
 * Le fichier rejoint les pièces déposées du dossier, là où atterrit déjà le
 * devis d'un compte rendu validé.
 */
const TYPOLOGIES_DEVIS = [
  ['', 'aucune — grille à remplir à la main'],
  ['RAC_I', 'RAC_I — immeuble'],
  ['RAC_C', 'RAC_C — chambre souterraine'],
  ['RAC_P', 'RAC_P — aérien poteau'],
  ['RAC_F', 'RAC_F — façade'],
  ['PTO_C', 'PTO_C — prise existante'],
];

function DevisManuel({ dossier, onCree }) {
  const typoDossier = String(dossier.identite?.typo || '').toUpperCase();
  const [ouvert, setOuvert] = useState(false);
  const [modele, setModele] = useState(dossier.type === 'SAV' ? 'sav' : 'cri');
  const [typologie, setTypologie] = useState(
    TYPOLOGIES_DEVIS.some(([v]) => v && v === typoDossier) ? typoDossier : '');
  const [lgPublic, setLgPublic] = useState('');
  const [lgPrive, setLgPrive] = useState('');
  const [decharge, setDecharge] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [msg, setMsg] = useState(null);
  const [devis, setDevis] = useState(null);
  const [saisie, setSaisie] = useState({});

  const chargerSaisie = (r) => {
    const s = {};
    (r.lignes || []).forEach(l => { s[l.cellule] = l.quantite === null ? '' : String(l.quantite); });
    setSaisie(s);
  };

  const creer = async () => {
    setEnCours(true); setErreur(null); setMsg(null);
    try {
      const r = await creerDevisDossier(dossier.id, {
        variante: modele, typologie,
        lg_public: lgPublic, lg_prive: lgPrive, no_decharge: decharge,
      });
      setDevis(r);
      chargerSaisie(r);
      // Le nom est déjà sous les yeux, en lien : le répéter n'apprend rien.
      setMsg('Devis créé et déposé dans le dossier');
      if (onCree) onCree();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Création impossible.');
    } finally { setEnCours(false); }
  };

  const enregistrer = async () => {
    setEnCours(true); setErreur(null); setMsg(null);
    try {
      const q = {};
      Object.entries(saisie).forEach(([c, v]) => { q[c] = v === '' ? null : Number(v); });
      const r = await setLignesDevisDossier(dossier.id, devis.fichier, q);
      setDevis({ ...devis, ...r });
      chargerSaisie(r);
      setMsg('Enregistré — total ' + r.total + ' €');
      if (onCree) onCree();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setEnCours(false); }
  };

  // Le total suit la saisie : attendre l'enregistrement pour le voir
  // obligerait à enregistrer pour juger du montant.
  const total = devis
    ? (devis.lignes || []).reduce((s, l) => s + (Number(saisie[l.cellule]) || 0) * (l.prix || 0), 0)
    : 0;

  const champ = {
    fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg)', color: 'var(--text)', width: '100%',
  };

  if (!ouvert) {
    return (
      <button type="button" onClick={() => setOuvert(true)}
        title="Monter un devis depuis les informations du dossier, sans attendre de compte rendu"
        style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 7,
                 border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)',
                 cursor: 'pointer' }}>
        Créer un devis
      </button>
    );
  }

  return (
    /* Ouvert, le bloc prend sa propre ligne : à côté du bouton de dépôt il
       n'aurait qu'un tiers de la largeur, et la grille des quantités y serait
       illisible. */
    <div style={{ flex: '1 1 100%', background: 'var(--bg3)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>Créer un devis</strong>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          en-tête rempli depuis le dossier — {dossier.identite?.ref_commande || dossier.id}
        </span>
        <button type="button" onClick={() => { setOuvert(false); setDevis(null); setErreur(null); setMsg(null); }}
          style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 5,
                   border: '1px solid var(--border)', background: 'var(--bg)',
                   color: 'var(--text2)', cursor: 'pointer' }}>
          fermer
        </button>
      </div>

      {erreur && <div className="alert alert-error" style={{ marginBottom: 8 }}>{erreur}</div>}

      {!devis && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 10, marginBottom: 10 }}>
            <label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Modèle</div>
              <select value={modele} onChange={e => setModele(e.target.value)} style={champ}>
                <option value="cri">Production (raccordement, VT)</option>
                <option value="sav">SAV — avec tirage au forfait</option>
              </select>
            </label>
            <label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Raccordement devisé</div>
              <select value={typologie} onChange={e => setTypologie(e.target.value)} style={champ}>
                {TYPOLOGIES_DEVIS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </select>
            </label>
            <label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Domaine public (ml)</div>
              <input type="number" min="0" step="1" value={lgPublic}
                onChange={e => setLgPublic(e.target.value)} style={champ} placeholder="0" />
            </label>
            <label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Domaine privé (ml)</div>
              <input type="number" min="0" step="1" value={lgPrive}
                onChange={e => setLgPrive(e.target.value)} style={champ} placeholder="0" />
            </label>
            <label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>N° de décharge</div>
              <input type="text" value={decharge} onChange={e => setDecharge(e.target.value)}
                style={champ} placeholder="facultatif" />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={creer} disabled={enCours}
              style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--accent, #2563eb)',
                       color: '#fff', cursor: enCours ? 'progress' : 'pointer' }}>
              {enCours ? 'Création…' : 'Créer le devis'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Sans typologie ni longueur, le devis sort en-tête rempli et grille vide : c’est ce
              qu’il faut pour une visite technique.
            </span>
          </div>
        </>
      )}

      {devis && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                        marginBottom: 8 }}>
            <a {...proprietesLien(devis.url, devis.fichier)}
              style={{ fontSize: 12.5, fontWeight: 600, wordBreak: 'break-all' }}>
              {devis.fichier}
            </a>
            {msg && <span style={{ fontSize: 11, color: 'var(--green2)', fontWeight: 600 }}>{msg}</span>}
          </div>
          {(devis.detail || []).length > 0 && (
            <ul style={{ margin: '0 0 8px', padding: '0 0 0 18px', fontSize: 11, color: 'var(--text3)' }}>
              {devis.detail.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 5 }}>
            Quantités — vider une case retire la ligne.
          </div>
          <div style={{ maxHeight: '36vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <tbody>
                {(devis.lignes || []).map(l => {
                  const q = Number(saisie[l.cellule]) || 0;
                  return (
                    <tr key={l.cellule} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '3px 6px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>{l.ref}</td>
                      <td style={{ padding: '3px 6px' }}>{l.libelle}</td>
                      <td style={{ padding: '3px 6px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {l.prix} € / {l.unite}
                      </td>
                      <td style={{ padding: '3px 6px', width: 80 }}>
                        <input type="number" min="0" step="0.01" value={saisie[l.cellule] ?? ''}
                          onChange={e => setSaisie(s => ({ ...s, [l.cellule]: e.target.value }))}
                          style={{ ...champ, fontSize: 11.5, padding: '3px 6px' }} />
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap',
                                   color: q ? 'var(--text)' : 'var(--text3)', fontWeight: q ? 600 : 400 }}>
                        {q ? (q * (l.prix || 0)).toFixed(2) + ' €' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>Total : {total.toFixed(2)} €</strong>
            <button type="button" onClick={enregistrer} disabled={enCours}
              style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--accent, #2563eb)',
                       color: '#fff', cursor: enCours ? 'progress' : 'pointer' }}>
              {enCours ? 'Enregistrement…' : 'Enregistrer les quantités'}
            </button>
            <button type="button" onClick={() => { setDevis(null); setMsg(null); }}
              style={{ fontSize: 12, padding: '6px 12px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--bg)',
                       color: 'var(--text2)', cursor: 'pointer' }}>
              En créer un autre
            </button>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Le montant ne part pas tout seul dans le CRM : reportez-le ci-dessus dans
              « Montant du devis forfaitaire » si c’est celui qui doit y figurer.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function Depot({ libelle, dossierId, onDepose, espace }) {
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);
  const champ = React.useRef(null);

  const choisir = async (e) => {
    const fichiers = e.target.files;
    if (!fichiers || !fichiers.length) return;
    setEnCours(true); setMsg(null);
    try {
      const r = await deposerPiece(dossierId, fichiers, espace);
      setMsg(['ok', `${r.count} pièce(s) déposée(s).`]);
      if (onDepose) onDepose(r.files || []);
    } catch (err) {
      setMsg(['ko', err.response?.data?.error || 'Dépôt impossible.']);
    } finally {
      setEnCours(false);
      if (champ.current) champ.current.value = '';
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
      <input ref={champ} type="file" multiple onChange={choisir} style={{ display: 'none' }} />
      <button type="button" disabled={enCours} onClick={() => champ.current && champ.current.click()}
        style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 7,
                 border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)',
                 cursor: enCours ? 'progress' : 'pointer' }}>
        {enCours ? 'Dépôt…' : libelle}
      </button>
      {msg && (
        <span style={{ fontSize: 11, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</span>
      )}
    </span>
  );
}

/**
 * Le serveur refuse une première fois quand la planification rendrait
 * intenable un rendez-vous déjà réservé ailleurs. On nomme ce qui serait
 * perdu, et on n'insiste que si la conduite d'activité le veut.
 *
 * Rend vrai quand elle a confirmé, faux sinon.
 */
function confirmerPerte(rep, geste) {
  // Toutes les demandes de confirmation ne parlent pas de créneaux perdus :
  // un dossier déjà planifié ailleurs n'a pas d'orphelin à montrer, et la
  // phrase d'origine s'affichait alors trouée. Le serveur a déjà rédigé son
  // motif — on le rend tel quel.
  if (!(rep.orphelins || []).length) {
    return window.confirm(geste + ' :\n\n' + (rep.error || 'Confirmation demandée.')
      + '\n\nContinuer quand même ?');
  }
  const lignes = (rep.orphelins || []).map(o =>
    '— ' + o.dept + ', le ' + String(o.date).split('-').reverse().join('/')
    + ' à ' + o.creneau + ' : réservé par un client, et ' + o.perd.join(', ')
    + ' ne pourra plus le tenir');
  return window.confirm(
    geste + ' rend un rendez-vous déjà réservé intenable :\n\n'
    + lignes.join('\n')
    + '\n\nIl faudra le replanifier avec le client, ou choisir un autre technicien.'
    + '\n\nContinuer quand même ?');
}

/**
 * Le créneau choisi par le client, et ce que le moteur en dit maintenant.
 *
 * La fiche affichait la date et l'heure sans dire qui pouvait y aller : il
 * fallait ouvrir « Planification RDV » pour le savoir, puis revenir saisir la
 * date, l'heure et le technicien à la main, et basculer l'étape. Tout tient
 * ici, et dans l'ordre qui protège le client : la date d'abord, l'étape
 * ensuite, parce que c'est elle qui fait partir la confirmation.
 *
 * Le moteur est interrogé à l'ouverture et de nouveau au clic : un créneau
 * libre il y a dix minutes a pu être pris depuis.
 */
function CreneauClient({ dossierId, date, heure, stage, datePosee, heurePosee, onValide }) {
  const [lu, setLu] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);
  // Le déverrouillage du déplacement, demandé explicitement.
  const [deplacer, setDeplacer] = useState(false);

  const charger = useCallback(() => {
    setErreur(null);
    getErdvFiche(dossierId).then(setLu)
      .catch(e => setErreur(e.response?.data?.error || 'Moteur illisible'));
  }, [dossierId]);

  useEffect(() => { charger(); }, [charger]);

  const memeJour = String(datePosee || '').slice(0, 10) === String(date || '').slice(0, 10);
  const memeHeure = String(heurePosee || '').slice(0, 5) === String(heure || '').slice(0, 5);
  const conforme = stage === 'Planifiée' && memeJour && memeHeure;

  // Déjà planifié, mais pas sur ce créneau : le bouton reste visible, parce
  // que réaligner le dossier sur l'E-RDV que Covage attend est une action
  // légitime. Il est grisé, parce que le faire par mégarde déplace le
  // rendez-vous d'un client et lui annonce une autre date. Mesuré le
  // 20/10/2026 : 97 des 200 dossiers « Planifiée » examinés sont posés à une
  // autre date que leur E-RDV, donc le bouton était actif sur la moitié d'eux.
  const deplacement = stage === 'Planifiée' && !conforme;

  const valider = async (confirme) => {
    setEnCours(true); setMsg(null); setErreur(null);
    try {
      const r = await validerErdvFiche(dossierId, confirme ? { confirme: true } : {});
      const t = String(r.ecrits.Technicien_OT || '').replace('VALEA-', '');
      setMsg(`Posé le ${String(r.ecrits.Date_de_l).split('-').reverse().join('/')} `
           + `à ${r.ecrits.Heure_de_l_intervention} avec ${t}, étape « Planifiée ».`);
      if (onValide) onValide();
      charger();
    } catch (e) {
      const rep = e.response?.data;
      if (rep?.confirmation_requise) {
        setEnCours(false);
        if (confirmerPerte(rep, 'Poser ce rendez-vous')) return valider(true);
        setErreur('Rendez-vous non posé.');
        return;
      }
      setErreur(rep?.error || 'Validation impossible');
    } finally { setEnCours(false); }
  };

  const ligne = { fontSize: 11, color: 'var(--text2)', marginTop: 5, lineHeight: 1.45 };

  return (
    <>
      {erreur && <div style={{ ...ligne, color: '#b91c1c' }}>{erreur}</div>}

      {lu && !lu.sans_erdv && !lu.illisible && (
        <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid rgba(46,160,67,.3)' }}>
          {lu.technicien_retenu ? (
            <div style={ligne}>
              Technicien : <strong>{lu.technicien_retenu}</strong>
              {lu.technicien_assigne_libre
                ? ' — déjà sur la commande, et libre sur ce créneau'
                : lu.technicien_assigne
                  ? ` — le moteur le recommande à la place de ${lu.technicien_assigne}, qui n’est pas libre`
                  : ' — recommandé par le moteur'}
              {lu.techniciens.length > 1 && ` (libres : ${lu.techniciens.join(', ')})`}
            </div>
          ) : (
            <div style={{ ...ligne, color: '#b45309' }}>{lu.blocage}</div>
          )}

          {lu.concurrents > 0 && (
            <div style={{ ...ligne, color: '#b45309' }}>
              {lu.concurrents} autre{lu.concurrents > 1 ? 's' : ''} rendez-vous se dispute
              {lu.concurrents > 1 ? 'nt' : ''} ce créneau : choisir ici engage leur technicien.
            </div>
          )}

          {conforme ? (
            <div style={{ ...ligne, color: 'var(--green2)', fontWeight: 600 }}>
              ✓ Rendez-vous posé à cette date, rien à faire.
            </div>
          ) : lu.technicien_retenu ? (
            <>
              {deplacement && (
                <div style={{ marginTop: 8, padding: '7px 9px', borderRadius: 6, fontSize: 10.5,
                              lineHeight: 1.45, color: '#ff8a7a',
                              background: 'rgba(212,43,43,.08)',
                              border: '1px solid rgba(212,43,43,.26)' }}>
                  <b style={{ color: '#ff6b6b' }}>Déjà planifié</b>
                  {datePosee ? ' le ' + String(datePosee).slice(0, 10).split('-').reverse().join('/') : ''}
                  {heurePosee ? ' à ' + String(heurePosee).slice(0, 5) : ''} — valider l’E-RDV
                  déplacerait le rendez-vous, et une nouvelle confirmation partirait au client.
                  {!deplacer && (
                    <button type="button" onClick={() => setDeplacer(true)}
                      style={{ display: 'block', marginTop: 5, background: 'none', padding: 0,
                               border: 'none', color: '#ff6b6b', fontSize: 10.5,
                               fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                      Déplacer quand même
                    </button>
                  )}
                </div>
              )}
              <button className="btn btn-sm" onClick={() => valider(false)}
                disabled={enCours || (deplacement && !deplacer)}
                title={deplacement && !deplacer
                  ? 'Le dossier est déjà planifié — utilisez « Déplacer quand même »'
                  : 'Pose la date, l’heure et le technicien, puis bascule en « Planifiée »'}
                style={{ marginTop: 8, color: '#fff', border: 'none',
                         background: (deplacement && !deplacer) ? '#4b5563' : '#15803d',
                         cursor: (deplacement && !deplacer) ? 'not-allowed' : 'pointer',
                         opacity: (deplacement && !deplacer) ? 0.65 : 1 }}>
                {enCours ? 'Validation…' : deplacement ? 'Déplacer sur l’E-RDV' : 'Valider ce RDV'}
              </button>
            </>
          ) : null}

          {!conforme && lu.technicien_retenu && (
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>
              Pose la date, l’heure et le technicien, puis bascule en « Planifiée ».
              La confirmation part alors au client et à Covage.
            </div>
          )}

          {msg && <div style={{ ...ligne, color: 'var(--green2)', fontWeight: 600 }}>{msg}</div>}
        </div>
      )}
    </>
  );
}

/**
 * La durée de l'intervention, quand la typologie se trompe.
 *
 * Le moteur E-RDV compte deux heures pour un raccordement. Une intervention
 * sur devis peut en demander cinq ou six : il offrait alors un créneau de 14 h
 * sur un technicien déjà pris pour la journée, et un client pouvait le
 * réserver. Poser la durée réelle ici ferme ce qu'il faut, et le moteur
 * repasse aussitôt.
 */
function DureeIntervention({ dossierId, duree, onEcrit }) {
  const d = duree || {};
  const [saisie, setSaisie] = useState(d.posee != null ? String(d.posee) : '');
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setSaisie(d.posee != null ? String(d.posee) : ''); }, [d.posee]);

  const envoyer = async (heures, confirme) => {
    setEnCours(true); setMsg(null);
    try {
      const r = await definirDureeFiche(dossierId, heures, confirme);
      setMsg(['ok', r.heures == null
        ? `Durée retirée — le moteur reprend ${d.defaut} h.`
        : `Durée posée à ${r.heures} h. Le moteur repasse.`]);
      if (onEcrit) onEcrit();
    } catch (e) {
      const rep = e.response?.data;
      // Le serveur refuse une première fois quand la durée rend intenable un
      // rendez-vous déjà réservé : on le montre, et on n'écrit que si la
      // conduite d'activité le veut quand même.
      if (rep?.confirmation_requise) {
        const lignes = (rep.orphelins || []).map(o =>
          `— ${o.dept}, le ${String(o.date).split('-').reverse().join('/')} à ${o.creneau} :`
          + ` réservé par un client, et ${o.perd.join(', ')} ne pourra plus le tenir`);
        setEnCours(false);
        if (window.confirm(
          'Cette durée rend un rendez-vous déjà réservé intenable :\n\n'
          + lignes.join('\n')
          + '\n\nIl faudra le replanifier avec le client.\n\nPoser la durée quand même ?')) {
          return envoyer(heures, true);
        }
        setMsg(['ko', 'Durée non posée.']);
        return;
      }
      setMsg(['ko', rep?.error || 'Enregistrement impossible.']);
    } finally { setEnCours(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>Durée de l’intervention</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="number" min="1" max="24" step="0.5" value={saisie} disabled={enCours}
          onChange={e => setSaisie(e.target.value)}
          placeholder={d.defaut != null ? String(d.defaut) : ''}
          style={{ width: 72, fontSize: 13, padding: '5px 8px', borderRadius: 6,
                   border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} />
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>heures</span>
        <button type="button" disabled={enCours || saisie === ''} onClick={() => envoyer(Number(saisie))}
          style={{ fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                   border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)' }}>
          {enCours ? '…' : 'Poser'}
        </button>
        {d.posee != null && (
          <button type="button" disabled={enCours} onClick={() => envoyer(null)}
            style={{ fontSize: 12, padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                     border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)' }}>
            revenir à {d.defaut} h
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
        {d.posee != null
          ? <>Le moteur compte <strong>{d.posee} h</strong> au lieu des {d.defaut} h de la typologie
              {d.par ? ` — posé par ${d.par}` : ''}.</>
          : <>Le moteur compte <strong>{d.defaut} h</strong>, d’après la typologie. Posez la durée réelle
              si l’intervention est plus longue : les créneaux E-RDV du technicien se ferment d’autant.</>}
      </div>
      {msg && (
        <span style={{ fontSize: 11, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</span>
      )}
    </div>
  );
}

/**
 * Les pièces du dossier déposées chez nous, avec leur suppression.
 *
 * L'onglet Devis n'affichait que le champ fichier de Zoho, alors que le dépôt
 * écrit dans data/pj/<dossier>/ : on joignait un devis, « 1 pièce déposée »
 * s'affichait, et la liste montrait toujours l'ancien. Deux magasins, un seul
 * montré. C'est celui-ci qu'on montre, puisque c'est celui où l'on écrit.
 *
 * Remplacer un devis demande de supprimer le précédent — sans quoi on ne sait
 * plus lequel des deux vaut.
 */
function PiecesDeposees({ dossierId, version, espace }) {
  const [pieces, setPieces] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let vivant = true;
    getPiecesDossier(dossierId, espace)
      .then(r => { if (vivant) setPieces(r.files || []); })
      .catch(() => { if (vivant) setPieces([]); });
    return () => { vivant = false; };
  }, [dossierId, version, espace]);

  const supprimer = async (nom) => {
    if (!window.confirm(`Supprimer « ${nom} » ?\n\nLe fichier est retiré du dossier, sans retour possible.`)) return;
    setMsg(null);
    try {
      const r = await supprimerPiece(dossierId, nom, espace);
      setPieces(r.files || []);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Suppression impossible.');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 7 }}>
      {pieces === null && <span style={{ fontSize: 12, color: 'var(--text3)' }}>lecture…</span>}
      {pieces && pieces.length === 0 && (
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>aucune pièce déposée ici</span>
      )}
      {(pieces || []).map(f => (
        <div key={f.name} style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
          <a {...proprietesLien(
                `/api/admin/pj/fichier/${dossierId}/${encodeURIComponent(f.name)}`
                + (espace ? `?espace=${espace}` : ''), f.name)}
            style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{f.name}</a>
          {f.size != null && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              {Math.max(1, Math.round(f.size / 1024))} ko
            </span>
          )}
          <button type="button" onClick={() => supprimer(f.name)} title="Supprimer"
            style={{ fontSize: 11, padding: '1px 7px', borderRadius: 5, cursor: 'pointer',
                     border: '1px solid var(--border)', background: 'var(--bg3)', color: '#b91c1c' }}>
            supprimer
          </button>
        </div>
      ))}
      {msg && <span style={{ fontSize: 11, color: '#b91c1c' }}>{msg}</span>}
    </div>
  );
}

/**
 * Le dépôt d'un arrêté de circulation, et sa preuve.
 *
 * Deux des vingt engagements de la grille Covage en dépendent et n'avaient
 * aucune valeur : Zoho n'a pas de champ de dépôt, et il n'en aura pas. La
 * déclaration se fait donc ici, le récépissé va dans les pièces du dossier.
 */
function BlocArcArs({ d, onFait }) {
  const a = d.arc_ars || {};
  const [depot, setDepot] = useState(a.date_depot || '');
  const [accord, setAccord] = useState(a.accord_recu_le || '');
  const [replanif, setReplanif] = useState(a.replanifie_le || '');
  const [preuve, setPreuve] = useState(a.preuve || '');
  const [arrete, setArrete] = useState(a.arrete || '');
  const [pieces, setPieces] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getPiecesDossier(d.id).then(r => setPieces(r.files || [])).catch(() => setPieces([]));
  }, [d.id]);

  const enregistrer = async () => {
    // Enregistrer n'est pas un geste neutre : un mail part chez Covage et le
    // dossier bascule en « Planifiée » si une date de replanification est
    // posée. On le dit avant, pas après.
    // Sans technicien affecté, le serveur pose la date mais laisse l'étape où
    // elle est : l'annoncer autrement ferait croire le rendez-vous confirmé.
    const bascule = replanif && replanif !== (a.replanifie_le || '');
    const effets = [
      depot && 'un mail part à Covage annonçant le dépôt du ' + depot.split('-').reverse().join('/'),
      bascule && 'le dossier est replanifié au ' + replanif.split('-').reverse().join('/')
        + ((d.identite && d.identite.technicien)
            ? ' et passe en « Planifiée »'
            : ' — l’étape ne basculera pas : aucun technicien n’est affecté'),
    ].filter(Boolean);
    if (effets.length && !window.confirm(
      'Enregistrer la déclaration ?\n\n'
      + effets.map(e => '— ' + e).join('\n')
      + (bascule && (d.identite && d.identite.technicien)
        ? '\n\nLe passage en « Planifiée » déclenche aussi les mails de planification.'
        : ''))) return;

    setEnCours(true); setMsg(null);
    try {
      const r = await saveArcArs(d.id, {
        date_depot: depot || null,
        accord_recu_le: accord || null,
        replanifie_le: replanif || null,
        preuve: preuve || null,
        arrete: arrete || null,
      });
      const dits = [];
      if (r.bascule) dits.push(`dossier replanifié au ${r.bascule.date.split('-').reverse().join('/')} et passé en « ${r.bascule.etape} »`);
      if (r.bascule_refusee) dits.push('date posée, mais étape inchangée — ' + r.bascule_refusee);
      if (r.mail && r.mail.destinataires) dits.push(`mail envoyé à ${r.mail.destinataires.join(', ')}`);
      if (r.mail && r.mail.erreur) dits.push(`mail NON parti : ${r.mail.erreur}`);
      setMsg([r.mail && r.mail.erreur ? 'ko' : 'ok',
        'Déclaration enregistrée' + (dits.length ? ' — ' + dits.join(' ; ') + '.' : '.')]);
      if (onFait) onFait(r.arc_ars);
    } catch (e) {
      setMsg(['ko', e.response?.data?.error || 'Enregistrement impossible.']);
    } finally { setEnCours(false); }
  };

  const delai = (debut, fin) => {
    if (!debut || !fin) return null;
    // Compte approché en jours ouvrés, pour afficher le délai sans refaire le
    // calendrier des fériés côté navigateur : le KPI, lui, compte juste.
    let n = 0;
    const d1 = new Date(debut + 'T12:00:00'), d2 = new Date(fin + 'T12:00:00');
    for (let x = new Date(d1); x < d2; x.setDate(x.getDate() + 1)) {
      const j = x.getDay();
      if (j !== 0 && j !== 6) n++;
    }
    return n;
  };

  const jDepot = delai(a.depart, depot);
  // Les dix jours ouvrés de replanification partent de la réception de
  // l'accord, pas du dépôt : sans accord déclaré, il n'y a rien à mesurer.
  const jReplanif = delai(accord, replanif);

  return (
    <div style={{ marginTop: 20, padding: '13px 15px', background: 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 9 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>
        Arrêté de circulation — dépôt, accord, arrêté
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
        Deux engagements de la grille Covage en dépendent : dépôt sous 2 jours ouvrés, puis
        replanification sous 10 jours ouvrés. Le dépôt se compte depuis la date d’intervention
        qui a révélé le besoin d’arrêté
        {a.depart
          ? <> — le <strong>{a.depart.split('-').reverse().join('/')}</strong> pour ce dossier
              {a.depart_intervention ? '' : ', faute de rendez-vous retrouvé : c’est la date d’entrée en attente de validation'}.</>
          : <>, et ce dossier n’est pas encore passé en « En attente validation DOSTEC/ARC ».</>}
        {' '}La replanification, elle, se compte depuis la réception de l’accord de la mairie :
        son instruction dépasse régulièrement dix jours, et ce temps-là n’est pas le nôtre. En
        pratique la demande oblige à annoncer une date à la mairie, donc le rendez-vous est posé
        dès le dépôt, à trois semaines : la date d’accord sert surtout à le démontrer.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '11px 18px' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Date de dépôt</div>
          <input type="date" value={depot} onChange={e => setDepot(e.target.value)}
            style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg2)', color: 'var(--text)' }} />
          {jDepot != null && (
            <div style={{ fontSize: 11, marginTop: 3, color: jDepot <= 2 ? 'var(--green2)' : '#b91c1c' }}>
              J+{jDepot} j.ouvrés{jDepot <= 2 ? '' : ' — hors engagement (2 j)'}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
            Accord de la mairie reçu le
          </div>
          <input type="date" value={accord} onChange={e => setAccord(e.target.value)}
            style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg2)', color: 'var(--text)' }} />
          <div style={{ fontSize: 11, marginTop: 3, color: 'var(--text3)' }}>
            {accord ? 'ouvre les 10 jours ouvrés de replanification' : 'tant qu’il manque, le dossier est « en attente d’accord »'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
            Date de replanification <span title="La mairie exige une date à la demande : on planifie en parallèle, à trois semaines">· 3 semaines en pratique</span>
          </div>
          <input type="date" value={replanif} onChange={e => setReplanif(e.target.value)}
            style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg2)', color: 'var(--text)' }} />
          {jReplanif != null ? (
            <div style={{ fontSize: 11, marginTop: 3, color: jReplanif <= 10 ? 'var(--green2)' : '#b91c1c' }}>
              J+{jReplanif} j.ouvrés après l’accord{jReplanif <= 10 ? '' : ' — hors engagement (10 j)'}
            </div>
          ) : replanif ? (
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--text3)' }}>
              délai non mesurable sans la date de l’accord
            </div>
          ) : null}
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Récépissé</div>
          <select value={preuve} onChange={e => setPreuve(e.target.value)}
            style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg2)', color: 'var(--text)', maxWidth: '100%' }}>
            <option value="">— aucune pièce choisie —</option>
            {pieces.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
          <div style={{ marginTop: 6 }}>
            <Depot libelle="Déposer le récépissé" dossierId={d.id}
              onDepose={fichiers => { setPieces(fichiers); if (fichiers[0]) setPreuve(fichiers[0].name); }} />
          </div>
        </div>
        {/* L'arrêté lui-même. Il va dans les pièces du dossier, donc le
            technicien l'a sur son téléphone sans qu'on ait rien à faire :
            son onglet « PJ » liste ce même répertoire. C'est la pièce qui
            l'autorise à occuper la voirie, il doit pouvoir la montrer. */}
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Arrêté délivré par la mairie</div>
          <select value={arrete} onChange={e => setArrete(e.target.value)}
            style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'var(--bg2)', color: 'var(--text)', maxWidth: '100%' }}>
            <option value="">— aucune pièce choisie —</option>
            {pieces.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
          <div style={{ marginTop: 6 }}>
            <Depot libelle="Déposer l’arrêté" dossierId={d.id}
              onDepose={fichiers => { setPieces(fichiers); if (fichiers[0]) setArrete(fichiers[0].name); }} />
          </div>
          <div style={{ fontSize: 11, marginTop: 4, color: arrete ? 'var(--green2)' : 'var(--text3)' }}>
            {arrete ? 'visible par le technicien dans l’onglet PJ du dossier'
                    : 'déposé ici, il apparaît tout seul dans l’onglet PJ du technicien'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 13, flexWrap: 'wrap' }}>
        <button type="button" disabled={enCours} onClick={enregistrer} className="btn btn-primary btn-sm">
          {enCours ? 'Enregistrement…' : 'Enregistrer et prévenir Covage'}
        </button>
        {!preuve && depot && (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            récépissé non joint — il peut l’être plus tard, le délai seul décide
          </span>
        )}
        {msg && <span style={{ fontSize: 11, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</span>}
      </div>

      {a.par && (
        <p style={{ margin: '9px 0 0', fontSize: 11, color: 'var(--text3)' }}>
          Déclaré par {a.par} le {new Date(a.at).toLocaleString('fr-FR')}.
        </p>
      )}
    </div>
  );
}

function Champ({ nom, valeur, mono }) {
  const vide = valeur === null || valeur === undefined || valeur === '' || valeur === false;
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{nom}</div>
      <div style={{
        fontSize: 13, marginTop: 1,
        fontWeight: vide ? 400 : 600,
        color: vide ? 'var(--text3)' : 'var(--text)',
        fontFamily: mono && !vide ? 'var(--mono)' : 'inherit',
        wordBreak: 'break-word',
      }}>{vide ? '—' : (valeur === true ? 'oui' : String(valeur))}</div>
    </div>
  );
}

// ── Recherche ────────────────────────────────────────────────────────────────

function Recherche({ onOuvrir, requete }) {
  const [q, setQ] = useState(requete || '');
  const [res, setRes] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Arrivee depuis la barre du haut : la requete est deja saisie, on ne va pas
  // demander de la retaper.
  useEffect(() => {
    const x = String(requete || '').trim();
    if (x.length < 3) return;
    setChargement(true); setErreur(null);
    rechercheFiche(x)
      .then(setRes)
      .catch(err => setErreur(err.response?.data?.error || 'Recherche impossible'))
      .finally(() => setChargement(false));
  }, [requete]);

  const lancer = async (e) => {
    if (e) e.preventDefault();
    if (q.trim().length < 3) { setErreur('Saisir au moins trois caractères'); return; }
    setChargement(true); setErreur(null);
    try { setRes(await rechercheFiche(q.trim())); }
    catch (err) { setErreur(err.response?.data?.error || 'Recherche impossible'); }
    finally { setChargement(false); }
  };

  return (
    <div className="page">
      <form onSubmit={lancer} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <label htmlFor="recherche-dossier" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Rechercher un dossier
        </label>
        <input id="recherche-dossier" value={q} onChange={e => setQ(e.target.value)} autoFocus
          placeholder="Réf O, réf W, FI, VIA, IMB, adresse, client, code postal, commentaire…"
          style={{ flex: '999 1 420px', minWidth: 0, padding: '10px 14px', fontSize: 14,
                   background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }} />
        <button type="submit" className="btn btn-primary" disabled={chargement}>
          {chargement ? 'Recherche…' : 'Rechercher'}
        </button>
      </form>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20, maxWidth: '72ch', lineHeight: 1.6 }}>
        Une seule zone : tape ce que tu as sous la main. Le résultat indique dans quel champ la
        correspondance a été trouvée, pour distinguer une vraie référence d'un mot croisé dans un commentaire.
      </div>

      {erreur && <div style={{ fontSize: 13, color: '#ff6b6b', marginBottom: 14 }}>{erreur}</div>}

      {res && (
        <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            <strong>{res.items.length}</strong> dossier{res.items.length > 1 ? 's' : ''}
            {res.total > res.items.length && <span style={{ color: 'var(--text3)' }}> sur {res.total} trouvés</span>}
            <span style={{ color: 'var(--text3)' }}> pour « {res.requete} »</span>
          </div>
          {res.items.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: 20 }}>Aucune correspondance.</div>
          )}
          <div style={{ display: 'grid', gap: 8 }}>
            {res.items.map(it => {
              const c = COULEUR_ETAPE[it.stage] || ['var(--bg3)', 'var(--text3)'];
              return (
                <button key={it.id} type="button" onClick={() => onOuvrir(it.id)}
                  style={{ textAlign: 'left', background: 'var(--bg2)', border: '1px solid var(--border)',
                           borderRadius: 9, padding: '12px 15px', cursor: 'pointer', color: 'var(--text)' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700 }}>{it.ref_commande}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text3)' }}>{it.ref_work_order}</span>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{it.client_final}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
                                   background: c[0], color: c[1] }}>{it.stage}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
                      {it.typo}{it.technicien_nom ? ' · ' + it.technicien_nom : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                    {[it.code_postal, it.ville].filter(Boolean).join(' ')}
                  </div>
                  {it.champ && it.extrait && (
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>
                      trouvé dans <strong style={{ color: 'var(--accent2)' }}>{it.champ}</strong> :{' '}
                      {it.extrait.avant}
                      <mark style={{ background: '#fde68a', color: '#1c1b19', padding: '0 2px', borderRadius: 2 }}>
                        {it.extrait.trouve}
                      </mark>
                      {it.extrait.apres}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Qualification SLA ────────────────────────────────────────────────────────

function Qualification({ dossierId, refWO, initial }) {
  const [etat, setEtat] = useState(initial || { erdv_client: false, delai_client: false, cr_conteste: false });
  const [msg, setMsg] = useState(null);

  const basculer = async (champ) => {
    const suivant = { ...etat, [champ]: !etat[champ] };
    setEtat(suivant); setMsg(null);
    try {
      const r = await saveQualification({
        dossierId, refWorkOrder: refWO,
        erdv_client: suivant.erdv_client, delai_client: suivant.delai_client,
        cr_conteste: suivant.cr_conteste,
      });
      setEtat(r.item || { erdv_client: false, delai_client: false, cr_conteste: false });
      setMsg('enregistré');
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      // Le motif compte : un refus de droits ne se corrige pas en réessayant,
      // et « échec » tout court a laissé la conduite d'activité cliquer dans
      // le vide sans savoir que la route lui était fermée.
      setEtat(etat);
      setMsg(e.response?.data?.error || 'échec de l’enregistrement');
    }
  };

  const actif = etat.erdv_client || etat.delai_client || etat.cr_conteste;
  const ligne = (champ, titre) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}>
      <input type="checkbox" checked={!!etat[champ]} onChange={() => basculer(champ)}
        style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
      <span style={{ fontSize: 12 }}>{titre}</span>
    </label>
  );

  return (
    <div style={{ background: actif ? 'rgba(219,139,0,.1)' : 'var(--bg2)',
                  border: '1px solid ' + (actif ? 'rgba(219,139,0,.4)' : 'var(--border)'),
                  borderRadius: 9, padding: '11px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
                       color: actif ? '#d98a00' : 'var(--text3)' }}>Sortie du calcul SLA</span>
        {msg && <span style={{ fontSize: 11, color: msg === 'enregistré' ? 'var(--green2)' : '#ff6b6b' }}>{msg}</span>}
      </div>
      {ligne('erdv_client', 'E-RDV non respecté suite demande client')}
      {ligne('delai_client', 'Délai allongé à la demande du client')}
      {ligne('cr_conteste', 'Refus de CR contesté — le compte rendu était conforme')}
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.45 }}>
        Une date avancée est reconnue automatiquement, elle n’a pas à être déclarée.
        Un refus contesté fait recompter le CRI comme conforme : l’étape « CR non conforme »
        reste dans l’historique, c’est le taux qui l’ignore.
      </div>
    </div>
  );
}

// ── La fiche ─────────────────────────────────────────────────────────────────

function Fiche({ id, onRetour }) {
  const [d, setD] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [section, setSection] = useState('synthese');
  const [onglet, setOnglet] = useState('planif');
  // Incrémenté à chaque dépôt : la liste des pièces se relit, au lieu de
  // laisser croire qu'il ne s'est rien passé.
  const [versionPieces, setVersionPieces] = useState(0);
  // Les pièces que le technicien voit sur le terrain. Leur nombre s'affiche
  // sur l'onglet : la conduite d'activité doit savoir qu'un dossier porte un
  // plan ou un arrêté sans avoir à l'ouvrir, ni à retaper la commande dans la
  // page « Pièces jointes ».
  const [piecesTech, setPiecesTech] = useState([]);
  // Les etapes viennent du serveur : c'est lui qui refusera une valeur
  // inconnue, autant ne proposer que ce qu'il accepte.
  const [meta, setMeta] = useState({ etapes: [], champs: {}, listes: {} });
  const [techs, setTechs] = useState([]);

  useEffect(() => {
    getMetaChampsFiche().then(setMeta).catch(() => {});
    getTechniciens().then(r => setTechs(r.techniciens || r || [])).catch(() => setTechs([]));
  }, []);

  useEffect(() => {
    if (!d || !d.id) return;
    let vivant = true;
    getPiecesDossier(d.id)
      .then(r => { if (vivant) setPiecesTech(r.files || []); })
      .catch(() => { if (vivant) setPiecesTech([]); });
    return () => { vivant = false; };
  }, [d && d.id, versionPieces]);

  /**
   * Relit le dossier.
   *
   * `discret` relit sans vider l'ecran : apres un enregistrement, la page
   * disparaissait puis revenait comme apres un F5, ce qui donnait a chaque
   * modification l'allure d'un incident.
   */
  const charger = useCallback((discret = false) => {
    if (!discret) setChargement(true);
    return getFiche(id)
      .then(setD)
      .catch(e => setErreur(e.response?.data?.error || 'Lecture impossible'))
      .finally(() => { if (!discret) setChargement(false); });
  }, [id]);

  const rafraichir = useCallback(() => charger(true), [charger]);

  // Une bascule en « Planifiée » fait partir les mails apres la reponse, pour
  // que le clic reponde tout de suite : un seul rafraichissement arrivait donc
  // avant eux, et l'onglet « E-mails » restait vide jusqu'au F5.
  const rafraichirApresEnvoi = useCallback(() => {
    charger(true);
    setTimeout(() => charger(true), 4000);
    setTimeout(() => charger(true), 12000);
  }, [charger]);

  useEffect(() => { charger(); }, [charger]);

  if (chargement) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Lecture du dossier…</div></div>;
  if (!d) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: '#ff6b6b' }}>{erreur}</div></div>;

  const id_ = d.identite, r = d.rdv;
  const palette = d.type === 'SAV' ? TEAL : AMBRE;
  const ce = COULEUR_ETAPE[id_.stage] || ['rgba(255,255,255,.85)', '#1C1B19'];
  const retour = d.precedent && d.nb_passages > 1;

  const ecartTexte = {
    aucun: ['✓ Les deux dates concordent', '#15803d', 'rgba(46,160,67,.12)'],
    heure: ['⚠ Heure différente de celle du client', '#b91c1c', 'rgba(255,107,107,.12)'],
    avance: ['Date avancée — ne compte pas comme non-respect', '#15803d', 'rgba(46,160,67,.12)'],
    autre_date: ['⚠ Date différente de celle choisie par le client', '#b45309', 'rgba(219,139,0,.14)'],
  }[r.ecart];

  return (
    <>
      {/* Bande d'identité */}
      <div style={{ background: palette.fond, padding: '14px 24px', display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={onRetour}
          style={{ background: 'rgba(255,255,255,.7)', border: 'none', borderRadius: 7, padding: '6px 11px',
                   fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#1C1B19' }}>← Recherche</button>
        {/* Tant que Zoho reste en service, la fiche doit pouvoir y renvoyer :
            c'est la seule facon de verifier une information ou de la corriger. */}
        <a href={`https://crm.zoho.eu/crm/tab/Potentials/${d.id}`} target="_blank" rel="noreferrer"
          style={{ background: 'rgba(255,255,255,.7)', borderRadius: 7, padding: '6px 11px', fontSize: 12,
                   fontWeight: 600, color: '#1C1B19', textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Zoho ↗
        </a>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Réf commande</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, color: '#1C1B19' }}>{id_.ref_commande || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Réf work order</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, color: '#1C1B19' }}>{id_.ref_work_order || '—'}</div>
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Client final</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1C1B19' }}>{id_.client_final || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Typologie</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1C1B19' }}>
              <Modifiable dossierId={d.id} champ="Typo_raccordement" valeur={id_.typo}
                options={d.type === 'SAV' ? meta.listes.typo_sav : meta.listes.typo_prod}
                onEcrit={rafraichir} rendu={v => v || '—'} />
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#15803D', color: '#fff' }}>
              <Modifiable dossierId={d.id} champ="Complexit" valeur={id_.complexite}
                options={meta.listes.Complexit} onEcrit={rafraichir} rendu={v => v || 'complexité'} />
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#1C1B19', color: '#F0D9A4' }}>
              <Modifiable dossierId={d.id} champ="Priorit" valeur={id_.priorite}
                options={meta.listes.Priorit} onEcrit={rafraichir} rendu={v => v || 'priorité'} />
            </span>
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Technicien</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginTop: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1C1B19' }}>
              <Modifiable dossierId={d.id} champ="Technicien_OT" valeur={id_.technicien}
                options={techs.map(t => ({
                  valeur: t.zoho_nom || t.nom_zoho || '',
                  libelle: [t.prenom, t.nom].filter(Boolean).join(' ') + ' · ' + (t.zoho_nom || t.nom_zoho || ''),
                })).filter(o => o.valeur)}
                onEcrit={rafraichir}
                rendu={() => id_.technicien_nom || id_.technicien || 'non affecté'} />
            </span>
            {id_.technicien && id_.stage === 'A planifier' && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                             background: 'rgba(255,255,255,.75)', color: '#7C2D12', whiteSpace: 'nowrap' }}>
                passage précédent
              </span>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: palette.label, fontWeight: 700 }}>Étape</div>
          <div style={{ display: 'inline-block', marginTop: 3, fontSize: 14, fontWeight: 700, padding: '4px 10px',
                        borderRadius: 7, background: ce[0], color: ce[1] }}>
            <Modifiable dossierId={d.id} champ="Stage" valeur={id_.stage}
              options={meta.etapes} onEcrit={rafraichirApresEnvoi}
              rendu={v => v || '—'} />
          </div>
        </div>
      </div>

      {/* Retour de dossier */}
      {retour && (
        <div style={{ background: 'rgba(219,139,0,.12)', borderBottom: '1px solid rgba(219,139,0,.3)',
                      padding: '10px 24px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                         background: '#b45309', color: '#fff', whiteSpace: 'nowrap' }}>
            {d.nb_passages}ᵉ passage
          </span>
          <div style={{ flex: '1 1 340px', fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
            Précédent le <strong>{jour(d.precedent.date)}</strong> avec <strong>{d.precedent.technicien_nom || '—'}</strong>
            {d.precedent.resultat === 'FAILED' && d.precedent.cause
              ? <> — échec : <strong>{d.precedent.cause}</strong></>
              : <> — {d.precedent.resultat === 'CLEARED' ? 'réalisé' : d.precedent.resultat}</>}
          </div>
        </div>
      )}

      {/* Les deux rendez-vous */}
      <div style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '12px 24px',
                    display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {r.erdv_date && !r.erdv_depasse && (
          <div style={{ flex: '1 1 280px', padding: '10px 14px', borderRadius: 9,
                        background: 'rgba(46,160,67,.12)', border: '1px solid rgba(46,160,67,.4)' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--green2)', fontWeight: 700 }}>
              🔒 Créneau choisi par le client
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{jour(r.erdv_date)} à {r.erdv_heure}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>E-RDV, non modifiable sans accord de Covage</div>
            <CreneauClient dossierId={d.id} date={r.erdv_date} heure={r.erdv_heure}
              stage={id_.stage} datePosee={r.date_posee} heurePosee={r.heure_posee}
              onValide={rafraichirApresEnvoi} />
          </div>
        )}
        {r.erdv_date && r.erdv_depasse && (
          <div style={{ flex: '1 1 280px', padding: '10px 14px', borderRadius: 9, background: 'var(--bg3)',
                        border: '1px dashed var(--border)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
            Date E-RDV dépassée ({jour(r.erdv_date)} à {r.erdv_heure}) — à replanifier.
          </div>
        )}
        <div style={{ flex: '1 1 280px', padding: '10px 14px', borderRadius: 9,
                      background: 'rgba(29,78,216,.1)', border: '2px solid #1D4ED8' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#1D4ED8', fontWeight: 700 }}>
            📅 Rendez-vous posé
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Modifiable dossierId={d.id} champ="Date_de_l" valeur={r.date_posee} type="date"
              onEcrit={rafraichir} rendu={v => (v ? jour(v) : 'non planifié')} />
            <Modifiable dossierId={d.id} champ="Heure_de_l_intervention" valeur={r.heure_posee} type="heure"
              onEcrit={rafraichir} rendu={v => (v ? 'à ' + v : 'sans heure')} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
            {id_.technicien_nom || id_.technicien || 'sans technicien'} · durée estimée {r.duree_estimee} h
          </div>
        </div>
        {ecartTexte && (
          <div style={{ flex: '0 1 240px', display: 'flex', alignItems: 'center', padding: '10px 14px',
                        borderRadius: 9, background: ecartTexte[2], fontSize: 12, fontWeight: 600,
                        color: ecartTexte[1], lineHeight: 1.45 }}>{ecartTexte[0]}</div>
        )}
      </div>

      <div className="page" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Colonne d'identité */}
        <aside style={{ flex: '1 1 280px', minWidth: 0, maxWidth: 330, background: 'var(--bg2)',
                        border: '1px solid var(--border)', borderRadius: 10, padding: '15px 17px' }}>
          <h2 style={{ margin: '0 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Commande</h2>
          <div style={{ display: 'grid', gap: 10 }}>
            <Champ nom="Type de work order" valeur={id_.type_work_order} />
            {/* Le declenchement d'un SAV commande tout : le delai a tenir, et
                le moteur qui propose les heures. Il n'etait lisible que dans
                l'onglet « Route optique », ou personne n'allait le chercher. */}
            {d.type === 'SAV' && d.sav && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Déclenchement SAV</div>
                <div style={{ fontSize: 13, marginTop: 1, fontWeight: 700 }}>
                  {d.sav.type_declenchement || <span style={{ color: 'var(--text3)', fontWeight: 400 }}>—</span>}
                  {d.sav.echeance && d.sav.echeance.limite && (
                    <span style={{ fontWeight: 600, fontSize: 11.5, marginLeft: 6,
                                   color: d.sav.echeance.limite < aujourdhui()
                                     ? '#b91c1c' : 'var(--green2)' }}>
                      à tenir avant le {jour(d.sav.echeance.limite)}
                    </span>
                  )}
                </div>
                {d.sav.echeance && d.sav.echeance.depuis && (
                  <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 1 }}>
                    déclenché le {jour(d.sav.echeance.depuis)}
                  </div>
                )}
              </div>
            )}
            <Champ nom="Client opérateur" valeur={id_.client_operateur} />
            <Champ nom="Offre" valeur={id_.offre} />
            <Champ nom="Réf PTO" valeur={id_.ref_pto} mono />
            <Champ nom="Opérateur commercial" valeur={id_.operateur_commercial} />
            <Champ nom="Intégrateur" valeur={id_.integrateur} />
            <Champ nom="Type d’offre" valeur={id_.type_offre} />
          </div>

          <h2 style={{ margin: '18px 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Dates clés</h2>
          <div style={{ display: 'grid', gap: 9 }}>
            <Champ nom="Réception de la commande" valeur={fmtDT(d.dates.creation_commande)} />
            <Champ nom="Attribution de la commande" valeur={fmtDT(d.dates.attribution_commande)} />
            <Champ nom="Prise de RDV" valeur={fmtDT(d.dates.prise_rdv)} />
            <Champ nom="Clôture" valeur={fmtDT(d.dates.cloture)} />
          </div>

          <h2 style={{ margin: '18px 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Adresse du site</h2>
          {/* Une adresse fausse bloque une intervention : il faut pouvoir la
              corriger sans ouvrir le CRM, meme si Covage en est la source. */}
          <div style={{ display: 'grid', gap: 8 }}>
            {[['N°', 'N', d.adresse.numero], ['Rue', 'Rue', d.adresse.rue],
              ['Code postal', 'Code_Postal', d.adresse.code_postal], ['Ville', 'Ville', d.adresse.ville],
              ['Étage', 'Etage', d.adresse.etage], ['Escalier', 'Escalier', d.adresse.escalier]]
              .map(([nom, champ, valeur]) => (
              <div key={champ}>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{nom}</div>
                <div style={{ fontSize: 13, marginTop: 1 }}>
                  <Modifiable dossierId={d.id} champ={champ} valeur={valeur || ''} onEcrit={rafraichir}
                    rendu={v => v || <span style={{ color: 'var(--text3)' }}>—</span>} />
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ margin: '18px 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Contact sur site</h2>
          <div style={{ display: 'grid', gap: 9 }}>
            <Champ nom="Nom" valeur={d.contact.nom} />
            <Champ nom="Téléphone" valeur={d.contact.telephone} />
            <Champ nom="Courriel" valeur={d.contact.mail} />
          </div>

          {/* Les trois sorties du calcul SLA sont celles de la production.
              Le SAV a ses propres engagements, et aucun ne se qualifie ainsi :
              proposer ces cases sur un SAV ne pouvait que tromper. */}
          {d.type !== 'SAV' && (
            <div style={{ marginTop: 18 }}>
              <Qualification dossierId={d.id} refWO={id_.ref_work_order} initial={d.qualification} />
            </div>
          )}
        </aside>

        {/* Onglets */}
        <main style={{ flex: '999 1 580px', minWidth: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div role="tablist" style={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: '6px 8px 0', borderBottom: '1px solid var(--border)' }}>
            {[
              ['planif', 'Planification'],
              ...(d.type === 'SAV' ? [['sav', 'Route optique']] : []),
              ['cri', `CRI (${d.cri.liste.length})`],
              ['pj', `Pièces jointes${piecesTech.length ? ` (${piecesTech.length})` : ''}`],
              ['commentaires', `Commentaires (${d.commentaires.length})`],
              ['devis', 'Devis'],
              ['contact', 'Contact client'],
              ['mails', `E-mails (${d.mails.length})`],
              ['vt', 'VT'],
              ['passages', `Passages (${d.nb_passages})`],
              ['etapes', 'Historique'],
            ].map(([k, nom]) => (
              <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
                style={{ fontSize: 13, fontWeight: 600, padding: '9px 14px', border: 'none', cursor: 'pointer',
                         borderRadius: '7px 7px 0 0', background: onglet === k ? 'var(--bg)' : 'transparent',
                         color: onglet === k ? 'var(--text)' : 'var(--text3)',
                         boxShadow: onglet === k ? 'inset 0 -2px 0 var(--accent)' : 'none' }}>{nom}</button>
            ))}
          </div>

          {onglet === 'planif' && (
            <div style={{ padding: '18px 20px' }}>
              {/* La proposition de date, la meme que dans Planification RDV :
                  la conduite d'activite decide ici, elle doit l'avoir ici. Le
                  composant se tait de lui-meme si un E-RDV valable existe. */}
              {id_.stage !== 'Planifiée' && (
                <div style={{ marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <PropositionDate dossierId={d.id} onPose={rafraichir} itv={{
                    code_postal: d.adresse.code_postal,
                    typo_raccordement: id_.typo,
                    technicien: id_.technicien,
                    date_intervention: r.date_posee,
                    // Le composant lit le format brut de Zoho ; la fiche a
                    // deja decoupe la date et l'heure.
                    date_erdv: r.erdv_date
                      ? `${r.erdv_date.split('-').reverse().join('-')} ${r.erdv_heure || '00:00'}:00`
                      : null,
                  }} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '12px 24px' }}>
                {[['1er appel sans succès', 'Date_heure_1er_appel_client_sans_succ_s', d.planification.premier_appel, 'horodatage'],
                  ['2e appel sans succès', 'Date_heure_2nd_appel_client_sans_succ_s', d.planification.second_appel, 'horodatage'],
                  // Un horodatage, pas du texte libre : le crayon ouvrait une
                  // saisie libre alors que Zoho attend une date et une heure.
                  ['Proposition de RDV', 'Heure_de_proposition_RDV', d.planification.proposition_rdv, 'horodatage'],
                  ['Mail de proposition envoyé le', 'Date_envoi_mail_proposition_RDV', d.planification.mail_proposition, 'horodatage'],
                  ['Type de planification', 'Type_de_planification', d.planification.type_planification, 'liste']]
                  .map(([nom, champ, valeur, type]) => (
                  <div key={champ}>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{nom}</div>
                    <div style={{ fontSize: 13, marginTop: 1 }}>
                      <Modifiable dossierId={d.id} champ={champ} valeur={valeur} type={type}
                        options={type === 'liste' ? meta.listes[champ] : undefined}
                        onEcrit={rafraichir}
                        rendu={v => (type === 'horodatage' ? fmtDT(v) : v) || <span style={{ color: 'var(--text3)' }}>—</span>} />
                    </div>
                  </div>
                ))}
              </div>
              {/* La duree tient avec la planification : c'est elle qui decide
                  des creneaux E-RDV que le technicien peut encore porter. */}
              <div style={{ marginTop: 15, padding: '13px 15px', background: 'var(--bg)',
                            border: '1px solid var(--border)', borderRadius: 9 }}>
                <DureeIntervention dossierId={d.id} duree={d.duree} onEcrit={rafraichir} />
              </div>

              {/* Ce qui met quelque chose en marche, juste sous le type de
                  planification pour qu'on le trouve la ou on decide. Les deux
                  cases d'apres ne font que constater. */}
              <div style={{ display: 'grid', gap: 13, marginTop: 15, padding: '13px 15px',
                            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 9 }}>
                <CocheAction dossierId={d.id} valeur={d.planification.mail_proposition_envoye}
                  champ="Envoi_mail_de_eproposition_RDV"
                  nom="Envoi mail de proposition RDV"
                  aide="Déclenche le workflow Zoho : le mail de proposition part au client."
                  confirmations={{
                    cocher: confirmationPropositionRDV(d.planification),
                    decocher: 'Le mail de proposition est déjà parti au client ; décocher la case ne le rappelle pas.\n\nDécocher quand même ?',
                  }} />
                <BlocReport d={d} onFait={rafraichir} />
                <BlocAbandon d={d} onFait={rafraichir} />
              </div>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginTop: 16, paddingTop: 14,
                            borderTop: '1px solid var(--border)' }}>
                <CocheEditable dossierId={d.id} champ="Envoi_mail_parsing_COVAGE"
                  nom="Mail normé Covage autorisé" valeur={d.planification.mail_norme_covage}
                  aide="Condition du mail normé au passage en « Planifiée »" onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="Envoi_mail_confirmation_RDV_client"
                  nom="Mail de confirmation client autorisé" valeur={d.planification.mail_confirmation_client}
                  aide="Condition de la confirmation au client" onEcrit={rafraichir} />
              </div>
              {/* Deux appels sans reponse ouvrent le droit a la demande : si elle
                  n'est pas partie, le dossier reste en attente pour rien. */}
              {d.planification.premier_appel && d.planification.second_appel
                && !d.abandon
                && id_.stage !== 'Commande gelée' && (
                <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: '#7C5A10',
                            background: 'rgba(219,139,0,.1)', border: '1px solid rgba(219,139,0,.3)',
                            borderRadius: 8, padding: '9px 12px' }}>
                  Deux appels sans succès sont consignés et la suppression du WO n’a pas été demandée à Covage.
                </p>
              )}
              {/* La trace de l'abandon vit chez nous : la case Zoho, elle, ne
                  reste jamais cochée, sa regle la redecoche apres l'envoi. */}
              {(!d.abandon && id_.stage === 'Commande gelée') && (
                <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: 'var(--text2)',
                            background: 'var(--bg)', border: '1px solid var(--border)',
                            borderRadius: 8, padding: '9px 12px' }}>
                  Commande gelée : la suppression du work order a été demandée à Covage avant que
                  la page ne tienne cette trace. Le dossier attend leur réponse.
                </p>
              )}
              {(d.planification.technicien_annonce || d.planification.technicien_annonce_tel) && (
                <>
                  <h3 style={{ margin: '18px 0 7px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>
                    Technicien annoncé au client
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '12px 24px' }}>
                    <Champ nom="Nom" valeur={d.planification.technicien_annonce} />
                    <Champ nom="Téléphone" valeur={d.planification.technicien_annonce_tel} />
                  </div>
                  {d.planification.technicien_annonce
                    && id_.technicien_nom
                    && d.planification.technicien_annonce !== id_.technicien_nom && (
                    <p style={{ margin: '8px 0 0', fontSize: 11, color: '#b45309' }}>
                      Le dossier est aujourd’hui affecté à {id_.technicien_nom} : le client attend {d.planification.technicien_annonce}.
                    </p>
                  )}
                </>
              )}
              {[['Commentaire de planification', d.planification.commentaire, 'Commentaire_planifd'],
                ['Commentaire interne', d.planification.commentaire_interne, 'Commentaire_interne'],
                ['Commentaire VALEA', d.commentaires.find(c => /VALEA/.test(c.nom))?.texte || '', 'Commentaire_CA_VALEA']]
                .map(([titre, texte, champ]) => (
                <div key={titre}>
                  <h3 style={{ margin: '18px 0 7px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>{titre}</h3>
                  <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px' }}>
                    <Modifiable dossierId={d.id} champ={champ} valeur={texte} type="texte_long"
                      onEcrit={rafraichir} style={{ width: '100%' }}
                      rendu={v => (
                        <span style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                          {v || <span style={{ color: 'var(--text3)' }}>aucun commentaire</span>}
                        </span>
                      )} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {onglet === 'sav' && (
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px', marginBottom: 20 }}>
                <Champ nom="Type de déclenchement" valeur={d.sav.type_declenchement} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Devis vierge</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    {d.sav.devis_vierge
                      ? <a {...proprietesLien(d.sav.devis_vierge)}>ouvrir</a>
                      : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </div>
                </div>
              </div>

              <h3 style={{ margin: '0 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>
                Route optique
              </h3>
              {d.sav.route_optique.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>
                  Aucune route optique importée pour ce SAV.
                </p>
              ) : d.sav.route_optique.map((rop, i) => (
                <div key={i} style={{ marginBottom: 18 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text3)' }}>Prise client</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px', marginBottom: 16 }}>
                    <Champ nom="Référence prise" valeur={rop.ref_prise} mono />
                    <Champ nom="Référence prestation (VIA)" valeur={rop.via} mono />
                  </div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text3)' }}>Point de branchement optique</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px', marginBottom: 16 }}>
                    <Champ nom="Référence PBO" valeur={rop.ref_pbo} mono />
                    <Champ nom="Localisation PBO" valeur={rop.adresse_pbo} />
                  </div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text3)' }}>Point de mutualisation</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px' }}>
                    <Champ nom="Référence PM technique" valeur={rop.ref_pm} mono />
                    <Champ nom="Localisation PM" valeur={rop.adresse_pm} />
                    <Champ nom="Tube" valeur={rop.tube} />
                    <Champ nom="Fibre" valeur={rop.fibre} />
                    <Champ nom="Position porte gauche" valeur={rop.porte_gauche} />
                    <Champ nom="Position porte droite" valeur={rop.porte_droite} />
                  </div>
                  {rop.commentaire && (
                    <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.6, background: 'var(--bg)',
                                border: '1px solid var(--border)', borderRadius: 8, padding: '10px 13px' }}>{rop.commentaire}</p>
                  )}
                </div>
              ))}
              <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                Importée du fichier Covage à la création du SAV. Le technicien y retrouve sa fibre sans ouvrir
                le fichier d’origine.
              </p>
            </div>
          )}

          {onglet === 'cri' && (
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px', marginBottom: 14 }}>
                <Champ nom="Statut du CRI" valeur={d.cri.statut} />
                <Champ nom="Clôturé à J+1" valeur={d.cri.cloture_j1 === null ? null : (d.cri.cloture_j1 ? 'oui' : 'non')} />
                <Champ nom="Typologie réalisée" valeur={d.cri.typo_effectuee} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Dernier PDF</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    {d.cri.pdf ? <a {...proprietesLien(d.cri.pdf)}>ouvrir</a> : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 18 }}>
                {/* La plus-value se coche à deux endroits, et c'est celle-ci
                    qui facture : le technicien peut se tromper, la conduite
                    d'activité relit et pose le cas réel. On montre à côté ce
                    que le compte rendu disait quand les deux divergent — 157
                    fois sur 2 363 comptes rendus payés. */}
                <div>
                  <CocheEditable dossierId={d.id} champ="FPV1"
                    nom="FPV — plus-value facturée"
                    aide={d.cri.fpv_cr === null || d.cri.fpv_cr === d.cri.fpv ? null
                      : d.cri.fpv_cr ? 'le technicien l’avait cochée sur son compte rendu'
                      : 'le technicien ne l’avait pas cochée sur son compte rendu'}
                    valeur={d.cri.fpv} onEcrit={rafraichir} />
                </div>
                {/* Ces trois contraintes sont des constats de terrain : c'est
                    nous qui les posons, donc elles se cochent ici. */}
                <CocheEditable dossierId={d.id} champ="DOSTEC"
                  nom="DOSTEC nécessaire" valeur={d.cri.dostec} onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="Arr_t_de_circulation_n_cessaire"
                  nom="ARC nécessaire" valeur={d.cri.arc} onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="Nacelle_a_pr_voir"
                  nom="Nacelle à prévoir" valeur={d.cri.nacelle} onEcrit={rafraichir} />
              </div>

              <h3 style={{ margin: '0 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Longueurs de câble</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '12px 24px', marginBottom: 18 }}>
                {Object.entries(d.cri.longueurs).map(([nom, v]) => (
                  <Champ key={nom} nom={nom} valeur={v != null && v !== '' ? v + ' ml' : null} />
                ))}
              </div>

              {[['Commentaire intervenant', d.cri.commentaire_intervenant],
                ['Commentaire d’échec de l’intervenant', d.cri.commentaire_echec]]
                .filter(([, v]) => v).map(([titre, texte]) => (
                <div key={titre} style={{ marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 7px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>{titre}</h3>
                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, background: 'var(--bg)', border: '1px solid var(--border)',
                              borderRadius: 8, padding: '11px 13px', whiteSpace: 'pre-line' }}>{texte}</p>
                </div>
              ))}

              <h3 style={{ margin: '0 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>
                Comptes rendus du dossier
              </h3>
              {d.cri.liste.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>Aucun compte rendu transmis.</p>
              ) : (
                <div style={{ display: 'grid', gap: 7 }}>
                  {d.cri.liste.map(c => (
                    <div key={c.id} style={{ display: 'flex', gap: 11, alignItems: 'center', flexWrap: 'wrap',
                                             padding: '9px 12px', borderRadius: 8, background: 'var(--bg)',
                                             border: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 125 }}>{c.date_soumission}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'var(--bg3)' }}>{c.type_cr}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                     background: c.resultat === 'CLEARED' ? 'rgba(46,160,67,.15)' : 'rgba(255,107,107,.15)',
                                     color: c.resultat === 'CLEARED' ? 'var(--green2)' : '#ff6b6b' }}>
                        {c.resultat === 'CLEARED' ? 'réalisé' : 'échec'}
                      </span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{c.ref_work_order}</span>
                      <span style={{ fontSize: 12 }}>{c.technicien}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                        {c.pdf_url && <a {...proprietesLien(c.pdf_url)} style={{ fontSize: 12 }}>PDF</a>}
                        {c.devis_url && <a {...proprietesLien(c.devis_url)} style={{ fontSize: 12 }}>devis</a>}
                        {c.dostec_url && <a {...proprietesLien(c.dostec_url)} style={{ fontSize: 12 }}>DOSTEC</a>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Depot libelle="Déposer un CRI" dossierId={d.id} />
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Les comptes rendus arrivent de l’application technicien après validation ; le dépôt manuel couvrira
                  les cas où ils n’y passent pas. Ouvert à la bascule en écriture.
                </span>
              </div>
              <BlocArcArs d={d} onFait={rafraichir} />
            </div>
          )}

          {onglet === 'commentaires' && (
            <div style={{ padding: '18px 20px' }}>
              {/* Trois commentaires sont a nous, donc modifiables ; les quatre
                  autres viennent de Covage, de l'operateur ou du technicien et
                  n'ont pas a etre reecrits ici. */}
              <div style={{ display: 'grid', gap: 14 }}>
                {[['Planification', 'Commentaire_planifd', d.planification.commentaire],
                  ['Conduite d’activité VALEA', 'Commentaire_CA_VALEA',
                    d.commentaires.find(c => /VALEA/.test(c.nom))?.texte || ''],
                  ['Interne', 'Commentaire_interne', d.planification.commentaire_interne]]
                  .map(([titre, champ, texte]) => (
                  <div key={champ}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em',
                                  color: 'var(--text3)', marginBottom: 4 }}>{titre}</div>
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)',
                                  borderRadius: 8, padding: '10px 13px' }}>
                      <Modifiable dossierId={d.id} champ={champ} valeur={texte} type="texte_long"
                        onEcrit={rafraichir} style={{ width: '100%' }}
                        rendu={v => (
                          <span style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                            {v || <span style={{ color: 'var(--text3)' }}>aucun commentaire</span>}
                          </span>
                        )} />
                    </div>
                  </div>
                ))}

                {d.commentaires.filter(c => !/VALEA|Planification|Interne/.test(c.nom)).map((c, i) => (
                  <div key={'ro' + i}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em',
                                  color: 'var(--text3)', marginBottom: 4 }}>
                      {c.nom} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· lecture seule</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line',
                                background: 'var(--bg)', border: '1px solid var(--border)',
                                borderRadius: 8, padding: '10px 13px' }}>{c.texte}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {onglet === 'pj' && (
            <div style={{ padding: '18px 20px' }}>
              <h3 style={{ margin: '0 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>
                Ce que le technicien voit sur le terrain
              </h3>
              <PiecesDeposees dossierId={d.id} version={versionPieces} />
              <div style={{ marginTop: 10 }}>
                <Depot libelle="Déposer une pièce" dossierId={d.id}
                  onDepose={() => setVersionPieces(v => v + 1)} />
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                Plans, arrêtés de circulation, consignes d’accès : ces fichiers apparaissent dans
                l’application du technicien, onglet « PJ » de son dossier. Les devis ne sont pas
                ici — ils portent les prix facturés à Covage et restent dans l’onglet Devis, que
                lui ne voit pas.
              </p>
            </div>
          )}

          {onglet === 'devis' && (
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 18 }}>
                <CocheEditable dossierId={d.id} champ="Devis_forfaitaire"
                  nom="Devis forfaitaire" valeur={d.devis.forfaitaire} onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="VT_incluse_dans_devis_forfaitaire"
                  nom="VT incluse dans le forfait" valeur={d.devis.vt_incluse} onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="Commande_sur_attachement_envoy"
                  nom="Commande sur attachement" valeur={d.devis.sur_attachement} onEcrit={rafraichir} />
                <CocheEditable dossierId={d.id} champ="Commande_factur_e"
                  nom="Commande facturée" valeur={d.devis.facturee} onEcrit={rafraichir} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '12px 24px' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Montant du devis forfaitaire</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    <Modifiable dossierId={d.id} champ="Montant_devis_forfaitaire"
                      valeur={d.devis.montant_forfaitaire ?? ''} onEcrit={rafraichir}
                      rendu={v => (v === '' || v === null ? <span style={{ color: 'var(--text3)' }}>—</span> : v + ' €')} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Date d’envoi du devis</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    <Modifiable dossierId={d.id} champ="Date_envoi_devis" type="date"
                      valeur={d.devis.date_envoi ? String(d.devis.date_envoi).slice(0, 10) : ''}
                      onEcrit={rafraichir}
                      rendu={v => (v ? jour(v) : <span style={{ color: 'var(--text3)' }}>—</span>)} />
                  </div>
                </div>
                <Champ nom="Total commande HT" valeur={d.devis.total_commande_ht != null ? d.devis.total_commande_ht + ' €' : null} />
              </div>

              <h3 style={{ margin: '22px 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Devis du dossier</h3>
              <PiecesDeposees dossierId={d.id} version={versionPieces} espace="devis" />
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                Ces fichiers restent dans le panel : ils portent les prix facturés à Covage et le
                technicien ne les voit pas. Pour lui déposer un plan, un arrêté ou une consigne,
                passez par la page « Pièces jointes ».
              </p>
              <div style={{ marginTop: 10, display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                <Depot libelle="Joindre un devis" dossierId={d.id} espace="devis"
                  onDepose={() => setVersionPieces(v => v + 1)} />
                {/* Le devis qu'on monte soi-même : même place que le dépôt,
                    puisqu'il finit au même endroit. */}
                <DevisManuel dossier={d} onCree={() => setVersionPieces(v => v + 1)} />
              </div>

              {/* Le champ fichier de Zoho, à part : nous ne pouvons pas y
                  supprimer une pièce, et le confondre avec notre dépôt est ce
                  qui faisait croire qu'un devis joint n'était pas arrivé. */}
              {(d.devis.pieces_jointes.length > 0 || d.devis.piece_jointe) && (
                <>
                  <h3 style={{ margin: '22px 0 9px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>Dans le CRM</h3>
                  <div style={{ display: 'grid', gap: 7 }}>
                    {d.devis.piece_jointe && (
                      <a {...proprietesLien(d.devis.piece_jointe)} style={{ fontSize: 13, fontWeight: 600 }}>
                        devis vierge
                      </a>
                    )}
                    {d.devis.pieces_jointes.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
                        <a {...proprietesLien(f.url, f.nom)} style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{f.nom}</a>
                        {f.taille && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{f.taille}</span>}
                      </div>
                    ))}
                  </div>
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                    Le téléchargement passe par le CRM : il demande une session Zoho ouverte, et la
                    suppression se fait là-bas.
                  </p>
                </>
              )}

              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                Le devis d’un compte rendu validé arrive ici tout seul. « Créer un devis » sert aux
                autres cas — chiffrer une visite technique, répondre à une demande avant
                intervention : l’en-tête est repris du dossier, seules les quantités restent à dire.
              </p>
            </div>
          )}

          {onglet === 'contact' && (
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {[['Client', [
                    ['Prénom', 'Pr_nom_du_contact_client', d.contact_brut.prenom, null],
                    ['Nom du contact', 'Nom_du_contact_client', d.contact_brut.nom, null],
                    ['Téléphone fixe', 'T_l_phone_fixe_du_contact', d.contact_brut.tel_fixe, null],
                    ['Téléphone mobile', 'T_l_phone_mobile_du_contact', d.contact_brut.tel_mobile, null],
                    ['Courriel', 'Mail_du_contact_clientf', d.contact_brut.mail, '{client.mail}'],
                    ['Courriel secondaire', 'Mail_du_contact_client_2', d.contact_brut.mail2, '{client.mail2}'],
                  ]],
                  ['Covage', [
                    ['Nom du support', 'Nom_du_support_KOSC', d.contact_brut.covage_nom, null],
                    ['Courriel du support', 'Mail_du_support_KOSC', d.contact_brut.covage_mail, '{covage.support}'],
                    ['Service delivery', 'Mail_du_service_delivery', d.contact_brut.covage_delivery, '{covage.delivery}'],
                  ]]].map(([titre, champs]) => (
                  <div key={titre} style={{ flex: '1 1 300px', minWidth: 0 }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text3)' }}>{titre}</h3>
                    <div style={{ display: 'grid', gap: 11 }}>
                      {champs.map(([nom, champ, valeur, jeton]) => (
                        <div key={champ}>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {nom}
                            {jeton && (
                              <code style={{ marginLeft: 6, fontFamily: 'var(--mono)', fontSize: 10,
                                             background: 'var(--bg3)', padding: '0 4px', borderRadius: 4,
                                             color: 'var(--blue2)' }}>{jeton}</code>
                            )}
                          </div>
                          <div style={{ fontSize: 13, marginTop: 1 }}>
                            <Modifiable dossierId={d.id} champ={champ} valeur={valeur || ''}
                              onEcrit={rafraichir}
                              rendu={v => v || <span style={{ color: 'var(--text3)' }}>—</span>} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                Le jeton bleu dit quelle variable chaque adresse alimente. Un mail ne part qu’aux
                variables inscrites dans ses destinataires, page Workflows : remplir « Service delivery »
                ne sert à rien si le mail n’appelle que {'{covage.support}'}. Une variable sans valeur
                est écartée, et l’onglet Email le signale.
              </p>
            </div>
          )}

          {onglet === 'mails' && (
            <div style={{ padding: '18px 20px' }}>
              {d.mails.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>Aucun mail automatique enregistré pour cette commande.</p>
              ) : (
                <div style={{ display: 'grid', gap: 7 }}>
                  {d.mails.map((m, i) => (
                    <div key={i} style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: 11, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
                                       background: 'var(--bg3)', whiteSpace: 'nowrap' }}>{m.titre}</span>
                        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtDT(m.at)}</span>
                        <span style={{ fontSize: 13 }}>{m.sujet}</span>
                        {m.simulation && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 9,
                                         background: 'var(--bg3)', color: 'var(--text3)' }}>simulation</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                        à {(m.destinataires || []).join(', ') || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                Journal de nos envois. Un envoi accepté par le serveur de messagerie n’est pas une preuve de lecture —
                un rejet arrive plus tard, dans la boîte d’expédition.
              </p>
            </div>
          )}

          {onglet === 'vt' && (
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '12px 24px' }}>
                <CocheEditable dossierId={d.id} champ="Visite_technique"
                  nom="Visite technique réalisée" valeur={d.vt.realisee} onEcrit={rafraichir} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Type de VT</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    <Modifiable dossierId={d.id} champ="Type_de_VT" valeur={d.vt.type || ''}
                      options={['Simple', 'Complexe']} onEcrit={rafraichir}
                      rendu={v => v || <span style={{ color: 'var(--text3)' }}>—</span>} />
                  </div>
                </div>
                <CocheEditable dossierId={d.id} champ="VT_incluse_dans_devis_forfaitaire"
                  nom="Incluse dans le devis forfaitaire" valeur={d.vt.incluse_devis} onEcrit={rafraichir} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Compte rendu de VT</div>
                  <div style={{ fontSize: 13, marginTop: 1 }}>
                    {d.vt.pdf ? <a {...proprietesLien(d.vt.pdf)}>ouvrir le PDF</a> : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Depot libelle="Déposer un compte rendu de VT" dossierId={d.id} />
                {!d.vt.realisee && (
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>Aucune visite technique sur ce dossier.</span>
                )}
              </div>
            </div>
          )}

          {onglet === 'passages' && (
            <div style={{ padding: '18px 20px' }}>
              {d.passages.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>Aucun compte rendu sur ce dossier.</p>
              ) : (
                <div style={{ display: 'grid', gap: 9 }}>
                  {d.passages.map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap',
                                          padding: '9px 12px', borderRadius: 8,
                                          background: p.actuel ? 'rgba(29,78,216,.08)' : 'var(--bg)',
                                          border: '1px solid ' + (p.actuel ? 'rgba(29,78,216,.3)' : 'var(--border)') }}>
                      <span style={{ fontSize: 12, fontWeight: 700, minWidth: 90 }}>{p.date}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text3)' }}>{p.ref_work_order}</span>
                      <span style={{ fontSize: 12 }}>{p.technicien_nom}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                     background: p.resultat === 'CLEARED' ? 'rgba(46,160,67,.15)' : 'rgba(255,107,107,.15)',
                                     color: p.resultat === 'CLEARED' ? 'var(--green2)' : '#ff6b6b' }}>
                        {p.resultat === 'CLEARED' ? 'réalisé' : 'échec'}
                      </span>
                      {p.cause && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{p.cause}</span>}
                      {p.actuel && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#1D4ED8', fontWeight: 600 }}>work order actuel</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {onglet === 'etapes' && (
            // La hauteur suit l'écran plutôt qu'un nombre fixe : 420 px
            // s'arrêtaient si tôt qu'on croyait l'historique tronqué, alors
            // qu'il fallait seulement faire défiler. Un dossier qui revient
            // plusieurs fois accumule vite trente lignes.
            <div style={{ padding: '18px 20px', maxHeight: '70vh', minHeight: 260, overflowY: 'auto' }}>
              {d.etapes_historique.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>Aucun historique local pour ce dossier.</p>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Étape</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Work order</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Intervention</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Technicien</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Modifié par</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Commentaire Covage</th>
                      <th style={{ padding: '4px 10px', fontWeight: 600 }}>Commentaire VALEA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.etapes_historique.slice().reverse().map((e, i) => {
                      const c = COULEUR_ETAPE[e.stage] || ['var(--bg3)', 'var(--text3)'];
                      return (
                        <tr key={i} style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                          {/* L'heure dit à quelle heure la commande est tombée :
                              une arrivée de 7 h et une de 18 h ne se planifient
                              pas le même jour. L'historique la porte depuis
                              toujours, la colonne la tronquait. */}
                          <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text3)' }}>
                            {String(e.date || '').slice(0, 10).split('-').reverse().join('/')}
                            {String(e.date || '').length > 10 && (
                              <span style={{ color: 'var(--text2)' }}> à {String(e.date).slice(11, 16).replace(':', 'h')}</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: c[0], color: c[1] }}>{e.stage}</span>
                          </td>
                          <td style={{ padding: '6px 10px', fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{e.ref_work_order}</td>
                          <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                            {e.date_intervention ? String(e.date_intervention).slice(0, 10) : ''}
                            {e.heure_intervention ? ' ' + e.heure_intervention : ''}
                          </td>
                          <td style={{ padding: '6px 10px' }}>{e.technicien_nom || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text3)' }}>{e.modifie_par || '—'}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text3)', maxWidth: 220 }}>{e.commentaire_covage || ''}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text3)', maxWidth: 220 }}>{e.commentaire_valea || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Données réseau */}
      <div className="page" style={{ paddingTop: 0 }}>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div role="tablist" style={{ display: 'flex', gap: 2, flexWrap: 'wrap', background: 'var(--bg3)', padding: '5px 8px 0' }}>
            {SECTIONS.map(([k, nom]) => (
              <button key={k} type="button" role="tab" aria-selected={section === k} onClick={() => setSection(k)}
                style={{ fontSize: 12, fontWeight: 600, padding: '8px 15px', border: 'none', cursor: 'pointer',
                         borderRadius: '7px 7px 0 0',
                         background: section === k ? 'var(--bg2)' : 'transparent',
                         color: section === k ? 'var(--text)' : 'var(--text3)' }}>{nom}</button>
            ))}
          </div>
          <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '13px 26px' }}>
            {Object.entries(d.reseau[section] || {}).map(([nom, valeur]) => (
              <Champ key={nom} nom={nom} valeur={valeur} mono={/Réf|Code|VIA|N°|GPS/.test(nom)} />
            ))}
          </div>
          {/* Les coordonnees seules ne servent a personne : c'est le lien vers
              la carte qui aide le technicien a trouver le point de branchement. */}
          {section === 'pbo' && d.reseau.gps && (
            <div style={{ padding: '0 18px 16px' }}>
              <a href={d.reseau.gps.maps} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', fontSize: 12 }}>
                📍 Voir le PBO sur la carte
              </a>
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
          Lu dans Zoho le {new Date(d.lu_le).toLocaleString('fr-FR')}. Les champs marqués ✎ s’écrivent
          dans Zoho à la validation, et refusent d’écraser une valeur qu’un tiers aurait modifiée
          depuis l’ouverture de la fiche.
        </div>
      </div>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Dossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">{id ? 'Dossier' : 'Dossiers'}</div>
      </div>
      {id
        ? <Fiche id={id} onRetour={() => navigate('/dossier')} />
        : <Recherche key={params.get('q') || ''} requete={params.get('q') || ''}
            onOuvrir={x => navigate('/dossier/' + x)} />}
    </>
  );
}
